import { Fragment, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import { animate, stagger } from "animejs";
import {
  Archive,
  ArrowRightLeft,
  Bot,
  BotOff,
  Check,
  ChevronDown,
  ChevronRight,
  Pencil,
  // DISABLED (for now): only used by the hidden list/board toggle.
  // Clock,
  // KanbanSquare,
  // List,
  ListChecks,
  Lock,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  UserRound,
  Users,
  // DISABLED (for now): only used by the hidden assign-conversation picker.
  // UserCog,
  X,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { AmbiguousMigrationDialog } from "@/components/messages/ambiguous-migration-dialog";
import { MergeResultDialog } from "@/components/messages/merge-result-dialog";
import { ThreadMemberDetailsDialog } from "@/components/messages/thread-member-details-dialog";
import { MessageClusterList } from "@/components/messages/message-cluster-list";
import { MergedSourcesFilter } from "@/components/messages/merged-sources-filter";
import { ArchivedMessageList } from "@/components/messages/archived-message-list";
import { DraftReplyAiDialog } from "@/components/messages/draft-reply-ai-dialog";
import { FlowSessionResetDialog } from "@/components/messages/flow-session-reset-dialog";
import { DeleteThreadDialog } from "@/components/messages/delete-thread-dialog";
import { RevokeMessageDialog } from "@/components/messages/revoke-message-dialog";
import { MessageComposerContainer } from "@/components/messages/message-composer";
import { ThreadListItem } from "@/components/messages/thread-list-item";
import { VirtualThreadList } from "@/components/messages/virtual-thread-list";
import { isVirtualThreadListActive } from "@/lib/messages/thread-list-mode";
import { ThreadFilterBar, ThreadWhatsAppNumberControl } from "@/components/messages/thread-filter-bar";
import { ThreadParticipantsPanel } from "@/components/messages/thread-participants-panel";
import { ThreadTasksPanel } from "@/components/messages/thread-tasks-panel";
import { SegmentedToggle } from "@/components/pages/calendar/facet-filter";
import { EditableConversationTitle } from "@/components/messages/editable-conversation-title";
import PresenceDot from "@/components/presence/presence-dot";
import { NewChatDialog } from "@/components/chats/new-chat-dialog";
import { NewPatientMessageDialog } from "@/components/messages/new-patient-message-dialog";
import { ForwardMessageDialog } from "@/components/messages/forward-message-dialog";
import { useMessagesPage } from "@/hooks/use-messages-page";
import { useHasPermission } from "@/hooks/use-permissions";
import { useUsableAvatarSrc } from "@/hooks/use-usable-avatar-image";
import { useHeaderSlot } from "@/components/layout/header-slot";
import { getAuthUser } from "@/lib/api/token-store";
import { useWorkspaceOwnerId } from "@/lib/workspaces/workspace-owner-context";
import {
  formatWaitShort,
  getBotAckWaitingMinutes,
  getMessageText,
  getPatientReplyWaitingMinutes,
  getThreadDisplayName,
  getThreadInitials,
  getWhatsAppInboxWaitingMinutes,
  groupThreadsByDay,
  isInternalDirectThread,
  isWhatsAppStaffAnswerMessage,
  TIER_STYLES,
  waitTier,
} from "@/lib/messages/messages-helpers";
import { getAvatarColorClasses } from "@/lib/messages/avatar-color";
import { getWaGroupInfo, isWaGroupThread } from "@/lib/messages/wa-groups";
// DISABLED (for now): thread assignment — assignment lives on tasks only.
// import { getThreadAssignee, isAssignedToUser } from "@/lib/thread-assignee";
import { getPatientTagDefinition, getPatientTagStrings } from "@/lib/patient-tags";

// Stable empty list so the composer's mention props don't churn re-renders.
const EMPTY_MENTION_CANDIDATES = [];
// DISABLED (for now): only used by the hidden header status chip + Done/Reopen buttons.
// import { getEffectiveThreadStatus, getThreadStatusDefinition } from "@/lib/thread-status";
// DISABLED (for now): thread assignment — assignment lives on tasks only.
// import { AssigneeChip } from "@/components/messages/assignee-chip";
import { AiHandlingBanner } from "@/components/messages/ai-handling-banner";
import { PatientTagPicker } from "@/components/messages/patient-tag-picker";
import { PatientEditorDialog } from "@/components/patients/patient-editor-dialog";
import { SwitchPatientDialog } from "@/components/patients/switch-patient-dialog";
// DISABLED (for now): only used by the hidden header Snooze button.
// import { SnoozeMenu } from "@/components/messages/snooze-menu";
import {
  THREAD_GROUP_FLAG_IGNORE,
  getGroupColor,
  getThreadGroupId,
  groupHasFlag,
} from "@/lib/thread-groups";
import { PinnedMessagesList } from "@/components/messages/pinned-messages-list";
import {
  getPinnedMessages,
  subscribePinnedMessages,
  togglePinMessage,
} from "@/lib/stores/pinned-messages-store";
import { requestOpenThreadMessage } from "@/lib/notifications/notification-navigation";
import { ChatSearchBar } from "@/components/messages/chat-search-bar";
import { ChatSearchResults } from "@/components/messages/chat-search-results";
import { CHAT_SEARCH_SCOPE_ALL, CHAT_SEARCH_SCOPE_THREAD, useChatMessageSearch } from "@/hooks/use-chat-message-search";

// Persisted across sessions/remounts so the user's choice to collapse the filter panel sticks.
const FILTERS_COLLAPSED_STORAGE_KEY = "healo-messages-filters-collapsed";

// Right (Participants/Tasks) panel geometry: collapsed matches the old fixed w-10 icon rail,
// the expanded default matches the old fixed w-[20rem]. Dragging the handle below the min
// width snaps down to the collapsed rail (same behavior as the nav sidebar and threads panel).
const RIGHT_PANEL_COLLAPSED_WIDTH_PX = 40;
const RIGHT_PANEL_EXPANDED_WIDTH = "320px";

export default function MessagesPage({ scope = "normal" }) {
  const m = useMessagesPage({ scope });
  // RBAC: below manage the page is read-only — no composer, no new-thread triggers. The
  // scope decides which module applies (patient Messages vs internal Chats).
  // A private contact chat is a personal conversation, not a clinical work item: the server gates
  // it on membership alone (requireDirectChatUser), so no module applies here either. Gating it on
  // `chats` would deny a nurse access to her own messages.
  const moduleCanManage = useHasPermission(scope === "internal" ? "chats" : "messages", "manage");
  const canManage = scope === "direct" ? true : moduleCanManage;
  // Archived history is its own module: view opens the in-thread archive affordances (the
  // backend gates the by-phone fetch on archive:view, so without this the button just 403s).
  const canViewArchive = useHasPermission("archive", "view");
  // The archive is the legacy 1:1 WhatsApp export, fetched by the thread's phone identity —
  // a group thread has no phone to key it, so the affordances stay hidden there regardless
  // of permission.
  const showArchiveAffordances = canViewArchive && !m.activeThreadIsWaGroup;
  // AI drafting is COMPOSED with canManage, never a substitute — the composer subtree only
  // renders at manage, so this module can only ever take the drafter away from someone who
  // could already read and send.
  const canUseAiRewrite = useHasPermission("ai_drafts", "view");
  const headerSlotEl = useHeaderSlot();
  const threadListViewportRef = useRef(null);
  const [threadListHasScrollbar, setThreadListHasScrollbar] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [newPatientMessageOpen, setNewPatientMessageOpen] = useState(false);
  // The open thread's patient can be edited (shared editor) or switched to another patient.
  const [patientEditorOpen, setPatientEditorOpen] = useState(false);
  const [switchPatientOpen, setSwitchPatientOpen] = useState(false);
  // Local "pin a message" state (client-only — see pinned-messages-store). Feeds the Pinned lens
  // list, the lens badge count, the filled-pin state on each message in the open thread, and
  // opening a pinned message back in its thread. Pinning is Messages-only (hidden in internal Chats).
  const [pinnedMessages, setPinnedMessages] = useState(() => getPinnedMessages());
  const pinnedOwnerUserId = useWorkspaceOwnerId();
  useEffect(() => {
    // Re-sync on mount AND on every Activity re-show: the store getter reads the CURRENT
    // user's pins, so a thaw (owner active again) heals any staleness from the frozen period.
    setPinnedMessages(getPinnedMessages());
    return subscribePinnedMessages((pins) => {
      // Owner guard: the profile-switch re-read emits while the OUTGOING pane is still
      // subscribed — without this, the incoming user's pins freeze into the outgoing
      // workspace and surface as their Pinned count/list when it thaws.
      if (pinnedOwnerUserId && String(getAuthUser()?.id || "").trim() !== pinnedOwnerUserId) return;
      setPinnedMessages(pins);
    });
  }, [pinnedOwnerUserId]);
  const pinnedCount = useMemo(
    () => pinnedMessages.filter((p) => p.scope === "normal").length,
    [pinnedMessages],
  );
  const pinnedMessageIds = useMemo(() => {
    const tid = String(m.selectedThreadId || "").trim();
    const ids = new Set();
    if (!tid) return ids;
    for (const p of pinnedMessages) {
      if (p.scope === "normal" && p.threadId === tid) ids.add(String(p.messageId));
    }
    return ids;
  }, [pinnedMessages, m.selectedThreadId]);
  const handleTogglePin = useCallback(
    (message) => {
      const threadId = String(m.selectedThreadId || "").trim();
      const messageId = message?.id ? String(message.id) : "";
      if (!threadId || !messageId) return;
      const threadTitle = m.selectedThreadForHeader
        ? getThreadDisplayName(m.selectedThreadForHeader, { isInternal: m.isScopedList }).label
        : "";
      const senderLabel = isWhatsAppStaffAnswerMessage(message)
        ? "STAFF ANSWERED"
        : m.isBotSender(message)
          ? "Bot"
          : m.getSenderLabel?.(message) || "";
      togglePinMessage({
        threadId,
        messageId,
        scope: m.isDirectScope ? "direct" : m.isInternalScope ? "internal" : "normal",
        createdAt: message?.createdAt || "",
        senderLabel,
        preview: getMessageText(message),
        threadTitle,
      });
    },
    [m.selectedThreadId, m.selectedThreadForHeader, m.isInternalScope, m.isBotSender, m.getSenderLabel],
  );
  const handleSelectPin = useCallback((pin) => {
    if (!pin?.threadId) return;
    requestOpenThreadMessage({ threadId: pin.threadId, messageId: pin.messageId });
  }, []);

  // ── In-chat message search ───────────────────────────────────────────────────────────────
  // Holds the enriched thread rows the search returned. A ref, because the reveal callback is an
  // input to the hook that PRODUCES those rows — depending on them directly would restart the
  // search on every fetched page.
  const chatSearchThreadsRef = useRef(null);
  // A result row carries its OWN threadId, which is what gets revealed: in the merged WhatsApp
  // view consecutive hits routinely come from different per-clinic threads, and the hook's
  // revealMessage un-hides a filtered-out clinic before scrolling.
  const handleRevealSearchHit = useCallback(
    (row) => {
      if (!row?.threadId || !row?.messageId) return;
      const thread = chatSearchThreadsRef.current?.get(row.threadId);
      m.revealMessage({
        threadId: row.threadId,
        messageId: row.messageId,
        botId: thread?.whatsappBotId,
      });
    },
    [m.revealMessage],
  );
  const chatSearch = useChatMessageSearch({
    scope,
    threadIds: m.searchableThreadIds,
    onReveal: handleRevealSearchHit,
  });
  chatSearchThreadsRef.current = chatSearch.threadsById;

  const chatSearchOpen = chatSearch.open;
  const { openSearch: openChatSearch, closeSearch: closeChatSearch } = chatSearch;
  const searchOwnerUserId = useWorkspaceOwnerId();
  // Read by the key handler, which is bound once: refs keep it off the effect's dependency list so
  // the listener is not torn down and re-added on every keystroke.
  const chatSearchOpenRef = useRef(chatSearchOpen);
  chatSearchOpenRef.current = chatSearchOpen;
  const closeChatSearchRef = useRef(closeChatSearch);
  closeChatSearchRef.current = closeChatSearch;
  const selectedThreadIdForSearchRef = useRef(m.selectedThreadId);
  selectedThreadIdForSearchRef.current = m.selectedThreadId;
  // Cmd/Ctrl+F opens search on the conversation, Cmd/Ctrl+Shift+F straight into All chats.
  // Scoped to the VISIBLE pane: frozen workspaces keep their listeners mounted, so without the
  // owner guard every background pane would also swallow the key.
  useEffect(() => {
    const onKeyDown = (event) => {
      const inThisPane =
        !searchOwnerUserId || String(getAuthUser()?.id || "").trim() === searchOwnerUserId;
      // Escape closes from anywhere in the search UI, not just the text field: after clicking a
      // result or a step arrow the focus is on a button, and a field-scoped handler would leave
      // Escape doing nothing. Skipped while a dialog or popover (the date picker) is up — those
      // own Escape first, and closing the whole search out from under them is not what was meant.
      if (event.key === "Escape") {
        if (!chatSearchOpenRef.current || !inThisPane) return;
        if (document.querySelector('[role="dialog"][data-state="open"]')) return;
        if (document.querySelector("[data-radix-popper-content-wrapper]")) return;
        event.preventDefault();
        closeChatSearchRef.current();
        return;
      }
      // event.key (not event.code) so the binding follows the user's keyboard layout, matching
      // the other letter shortcuts in the app.
      if (String(event.key).toLowerCase() !== "f") return;
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      if (!inThisPane) return;
      // The search bar lives in the conversation column, which is replaced by the "select a
      // thread" placeholder when nothing is open. Leave the key unhandled there rather than
      // swallowing it into a bar that cannot render.
      if (!selectedThreadIdForSearchRef.current) return;
      // A modal dialog owns the keyboard while it is up (and may have its own find field).
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      event.preventDefault();
      // Chromium binds nothing to Cmd+F in Electron, but a focused input would still receive it.
      event.stopPropagation();
      openChatSearch(event.shiftKey ? CHAT_SEARCH_SCOPE_ALL : CHAT_SEARCH_SCOPE_THREAD);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [openChatSearch, searchOwnerUserId]);

  // Opening/closing the bar changes the message pane's height. A reader parked at the newest
  // message must stay there, or the conversation appears to scroll itself.
  useEffect(() => {
    const viewport = m.chatScrollAreaRef.current;
    if (!viewport) return;
    if (viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight > 120) return;
    requestAnimationFrame(() => {
      const el = m.chatScrollAreaRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
    // Only the open/closed transition matters; typing must never yank the viewport.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatSearchOpen]);

  // Leaving a conversation makes CONVERSATION-scoped hits stale, so close. All-chats results stay
  // up on purpose: clicking a hit navigates to its thread, and closing there would make the panel
  // usable for exactly one result.
  const chatSearchIsAllChats = chatSearch.allChats;
  useEffect(() => {
    if (chatSearchOpen && !chatSearchIsAllChats) closeChatSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.selectedThreadId]);
  // Forward-message dialog: available in BOTH panes when the user can send somewhere (staff
  // targets need chats:manage, patient targets need messages:manage — the dialog narrows its
  // own tabs to what the user holds). The source snapshot (thread + title + scope) is taken
  // when the dialog opens, mirroring the pin-snapshot approach, so the provenance stamp on the
  // forwarded copy renders without ever loading the source thread.
  const canForwardToStaff = useHasPermission("chats", "manage");
  const canForwardToPatient = useHasPermission("messages", "manage");
  const canForwardMessages = canForwardToStaff || canForwardToPatient;
  const [forwardSource, setForwardSource] = useState(null);
  // Bumped once a batch has actually gone out, so the transcript drops its selection. A cancelled
  // dialog leaves it alone — re-ticking a dozen messages after a mis-click is the annoying part.
  const [forwardSelectionResetToken, setForwardSelectionResetToken] = useState(0);
  const handleForwardMessages = useCallback(
    (messages) => {
      const list = (Array.isArray(messages) ? messages : [messages]).filter(Boolean);
      if (!list.length) return;
      // Merged WhatsApp view: the message may belong to a sibling clinic thread — trust the
      // message's own threadId over the selected one so the backlink lands on the right thread.
      // Only a fallback for the batch: buildForwardedStamp prefers each message's own threadId.
      const threadId = String(list[0]?.threadId || m.selectedThreadId || "").trim();
      const threadTitle = m.selectedThreadForHeader
        ? getThreadDisplayName(m.selectedThreadForHeader, { isInternal: m.isScopedList }).label
        : "";
      setForwardSource({
        messages: list,
        threadId,
        scope: m.isScopedList ? "internal" : "patient",
        threadTitle,
      });
    },
    [m.selectedThreadId, m.selectedThreadForHeader, m.isInternalScope],
  );
  const handleForwardMessage = useCallback(
    (message) => handleForwardMessages([message]),
    [handleForwardMessages],
  );

  // "Delete for everyone" — held here until confirmed, since it cannot be undone. Two transports
  // behind one dialog: on WhatsApp it is the revoke (reaches the patient's phone; gated on the
  // same permission as sending — whoever can send the message can take it back); in internal
  // Chats it is the unsend of one's OWN message (the bubble disappears for every member; author-
  // only, no permission beyond membership — the cluster list offers it only on own messages and
  // the server re-checks authorship).
  const [revokeTarget, setRevokeTarget] = useState(null);
  const handleConfirmDeleteForEveryone = useCallback(() => {
    const message = revokeTarget;
    setRevokeTarget(null);
    if (!message) return;
    // Merged WhatsApp view: trust the message's own threadId over the selected one.
    const threadId = String(message?.threadId || m.selectedThreadId || "").trim();
    const messageId = String(message?.id || "");
    // WhatsApp → gateway revoke (async verdict); everything else (internal Chats, Healo-app
    // patient conversations) → unsend in place.
    if (m.activeThreadIsWhatsApp) m.handleRevokeMessage(threadId, messageId);
    else m.handleUnsendMessage(threadId, messageId);
  }, [revokeTarget, m.selectedThreadId, m.activeThreadIsWhatsApp, m.handleUnsendMessage, m.handleRevokeMessage]);
  const pinnedLensActive = !m.threadSearchActive && !m.isScopedList && m.threadLens === "pinned";
  // Collapse state for the filter panel. Lifted here (out of ThreadFilterBar) because the toggle now
  // lives in the shared app header while the panel it controls renders down in the thread column.
  const [filtersCollapsed, setFiltersCollapsed] = useState(() => {
    try {
      return window.localStorage?.getItem(FILTERS_COLLAPSED_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggleFiltersCollapsed = () => {
    setFiltersCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage?.setItem(FILTERS_COLLAPSED_STORAGE_KEY, next ? "1" : "0");
      } catch {
        // ignore storage errors (private mode / quota)
      }
      return next;
    });
  };

  const threadListRef = useRef(null);
  const prevFilterKeyRef = useRef("");
  const initialThreadAnimDone = useRef(false);
  const [headerPhotoModalOpen, setHeaderPhotoModalOpen] = useState(false);
  const { label: headerAvatarDisplayName, isPlaceholder: headerAvatarIsPlaceholder } = m.selectedThreadForHeader
    ? getThreadDisplayName(m.selectedThreadForHeader, { isInternal: m.isScopedList })
    : { label: m.conversationHeaderTitle, isPlaceholder: false };
  // Mirror the thread-list avatar: for a 1:1 chat seed from the other person's name (thread.title)
  // rather than the generic stored "Direct Chat" name, so the header shows their initials/colour.
  const headerIsDirectThread = String(m.selectedThreadForHeader?.type || "").toLowerCase() === "direct";
  const headerAvatarName = headerIsDirectThread
    ? headerAvatarDisplayName || m.selectedThreadForHeader?.name || ""
    : headerAvatarIsPlaceholder
      ? headerAvatarDisplayName
      : m.selectedThreadForHeader?.name || headerAvatarDisplayName || "";
  const headerAvatarSeed = headerAvatarName || m.conversationHeaderTitle;
  const headerAvatarInitials = getThreadInitials(headerAvatarSeed || m.conversationHeaderTitle);
  // Online presence for the selected 1:1 Chats thread: green dot next to the person's name when
  // the other member currently has the app open.
  const headerOtherMemberId = (() => {
    const headerThread = m.selectedThreadForHeader;
    if (!m.isInternalScope || !headerThread) return "";
    if (String(headerThread?.type || "").toLowerCase() !== "direct") return "";
    const meId = String(getAuthUser()?.id || "").trim();
    const ids = Array.isArray(headerThread?.userIds) ? headerThread.userIds.map((id) => String(id || "").trim()) : [];
    return ids.find((id) => id && id !== meId) || "";
  })();
  const headerShowOnlineDot = Boolean(
    headerOtherMemberId && m.onlineUserIds instanceof Set && m.onlineUserIds.has(headerOtherMemberId),
  );
  const headerAvatarColorClasses = getAvatarColorClasses(headerAvatarSeed);
  // Only a real photo is worth enlarging: a placeholder avatar renders as initials (see
  // useUsableAvatarSrc), so the enlarge affordance has to disappear with it.
  const headerPhotoUrl = useUsableAvatarSrc(m.selectedThreadForHeader?.avatarUrl);
  // WhatsApp group conversation: the subtitle becomes a "N participants" button into the
  // Participants panel, and the avatar fallback shows a Users glyph — initials seeded from a
  // group subject would masquerade as a person.
  const headerIsWaGroup = !m.isScopedList && isWaGroupThread(m.selectedThreadForHeader);
  const headerWaGroupParticipantCount = headerIsWaGroup
    ? getWaGroupInfo(m.selectedThreadForHeader).participantCount
    : 0;

  // "X is typing…" label for the open chat's header. Names resolve from the loaded
  // member rows (first names keep it short); unknown ids fall back to "Someone".
  const headerTypingLabel = useMemo(() => {
    const ids = m.headerTypingUserIds || [];
    if (!m.isInternalScope || ids.length === 0) return "";
    const rows = m.threadMembersGrouped?.direct || [];
    const nameById = new Map(
      rows.map((r) => [String(r?.id || ""), String(r?.label || "").trim().split(/\s+/)[0] || "Someone"]),
    );
    const names = ids.map((id) => nameById.get(String(id)) || "Someone");
    if (names.length === 1) return `${names[0]} is typing…`;
    if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
    return "Several people are typing…";
  }, [m.isInternalScope, m.headerTypingUserIds, m.threadMembersGrouped]);

  // @mention candidates for the open internal chat: the other loaded members (never
  // yourself). Labels double as the render-side emphasis vocabulary.
  const mentionCandidates = useMemo(() => {
    if (!m.isInternalScope) return EMPTY_MENTION_CANDIDATES;
    const rows = m.threadMembersGrouped?.direct || [];
    const out = [];
    for (const row of rows) {
      if (!row || row.isSelf || row.isBot) continue;
      const id = String(row.id || "").trim();
      const label = String(row.label || "").trim();
      if (id && label) out.push({ id, label });
    }
    return out.length ? out : EMPTY_MENTION_CANDIDATES;
  }, [m.isInternalScope, m.threadMembersGrouped]);
  const mentionNames = useMemo(
    () => (mentionCandidates.length ? mentionCandidates.map((c) => c.label) : null),
    [mentionCandidates],
  );

  // People the caller already has a 1:1 internal chat with — hidden in the New chat
  // dialog's Direct mode (that conversation already sits in the list; a soft-deleted 1:1
  // drops off this set, so its partner reappears and re-creating revives the thread).
  const existingDirectPartnerIds = useMemo(() => {
    if (!m.isInternalScope) return null;
    const meId = String(getAuthUser()?.id || "").trim();
    const out = new Set();
    for (const t of m.sortedThreads || []) {
      if (!isInternalDirectThread(t)) continue;
      const ids = Array.isArray(t?.userIds) ? t.userIds : [];
      const other = ids.map((id) => String(id || "").trim()).find((id) => id && id !== meId);
      if (other) out.add(other);
    }
    return out;
  }, [m.isInternalScope, m.sortedThreads]);

  // Workflow status / tags / tasks cluster for the conversation header (Messages scope only).
  const headerThreadId = String(m.selectedThreadForHeader?.threadId || m.selectedThreadId || "").trim();
  const headerIsTriageScope = !m.isScopedList && Boolean(m.selectedThreadForHeader);
  // DISABLED (for now): the header status chip + Snooze/Done/Reopen quick actions — in the
  // current logic the workflow status is derived automatically, so the header keeps only the
  // Tasks toggle.
  // const headerEffectiveStatus = headerIsTriageScope
  //   ? getEffectiveThreadStatus(m.selectedThreadForHeader, m.listClockMs)
  //   : null;
  // const headerStatusDef = headerEffectiveStatus ? getThreadStatusDefinition(headerEffectiveStatus) : null;
  // DISABLED (for now): header assignee — thread assignment is hidden; assignment
  // lives on tasks only.
  // const headerAssignee = headerIsTriageScope ? getThreadAssignee(m.selectedThreadForHeader) : null;
  // const headerAssigneeIsMe = Boolean(headerAssignee && m.isCurrentUserId(headerAssignee.id));
  const headerPatientTags = headerIsTriageScope ? getPatientTagStrings(m.selectedThreadForHeader) : [];
  const headerPatientUserId = String(m.selectedThreadForHeader?.patientUserId || "").trim();
  const headerOpenTaskCount =
    m.threadTasks.length > 0
      ? m.threadTasks.filter((t) => t.status === "todo").length
      : Number(m.selectedThreadForHeader?.tasksOpen) || 0;
  const tasksPanelShowing = m.rightPanelOpen && m.rightPanelMode === "tasks";
  const toggleTasksPanel = () => {
    if (!tasksPanelShowing) {
      m.setRightPanelMode("tasks");
      m.setRightPanelOpen(true);
    } else {
      m.setRightPanelOpen(false);
    }
  };

  // Resizable right panel (Participants/Tasks). Width is owned by the ResizablePanel; the
  // existing rightPanelOpen state keeps owning WHICH content renders (full panel vs icon rail),
  // so the show/hide buttons keep working — they flip the state and the effect below resizes
  // the panel to match. Same collapse pattern as the nav sidebar in App.jsx.
  const rightPanelRef = useRef(null);
  // Frozen at mount: defaultSize only matters for the initial layout, and keeping it
  // referentially stable avoids re-registering the panel on every collapse/expand.
  const [initialRightPanelSize] = useState(() =>
    m.rightPanelOpen ? RIGHT_PANEL_EXPANDED_WIDTH : `${RIGHT_PANEL_COLLAPSED_WIDTH_PX}px`,
  );
  const handleRightPanelResize = (panelSize, _id, prevPanelSize) => {
    // Skip the mount-time layout report (prev is undefined): the panel remounts on thread
    // deselect/reselect at the frozen defaultSize, and syncing that into rightPanelOpen would
    // re-open a panel the user hid. The effect below reconciles the width instead.
    if (!prevPanelSize) return;
    if (panelSize.inPixels > 0) {
      m.setRightPanelOpen(panelSize.inPixels > RIGHT_PANEL_COLLAPSED_WIDTH_PX + 1);
    }
  };
  useEffect(() => {
    const panel = rightPanelRef.current;
    if (!panel) return;
    const collapsedNow = panel.getSize().inPixels <= RIGHT_PANEL_COLLAPSED_WIDTH_PX + 1;
    if (m.rightPanelOpen && collapsedNow) {
      panel.expand();
      // expand() no-ops when the panel mounted directly at its collapsed size, so force the width.
      if (panel.getSize().inPixels <= RIGHT_PANEL_COLLAPSED_WIDTH_PX + 1) {
        panel.resize(RIGHT_PANEL_EXPANDED_WIDTH);
      }
    } else if (!m.rightPanelOpen && !collapsedNow) {
      panel.collapse();
    }
    // selectedThreadId is a dep because the panel unmounts with the conversation area: on
    // reselect it mounts at the frozen defaultSize, which may disagree with rightPanelOpen.
  }, [m.rightPanelOpen, m.selectedThreadId]);

  useEffect(() => {
    const viewport = threadListViewportRef.current;
    if (!viewport) return undefined;
    const content = viewport.firstElementChild;

    const update = () => {
      setThreadListHasScrollbar(viewport.scrollHeight > viewport.clientHeight + 1);
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(viewport);
    if (content) {
      ro.observe(content);
    }

    return () => ro.disconnect();
  }, [
    m.sortedThreads.length,
    m.filteredSortedThreads.length,
    // Virtual list: content height follows the exact total (and generation swaps), not loaded
    // row counts.
    m.threadListWindow.total,
    m.threadListWindow.generation,
    m.threadLens,
    m.threadFilterLockedOnly,
    m.threadFilterAwaitingReplyOnly,
    m.threadFilterWhatsappOnly,
    m.threadFilterAppOnly,
    m.threadFlagsError,
    m.threadGroupsError,
    m.threadListView,
    m.threadGroups.length,
    m.collapsedSections,
  ]);

  const filterKey = `${m.threadFilterLockedOnly}:${m.threadFilterAwaitingReplyOnly}:${m.threadFilterWhatsappOnly}:${m.threadFilterAppOnly}:${m.threadListView}`;

  // Server-fed attention rows can exist even when the windowed page (or the filtered page) is
  // empty — they cover the whole inbox, not just the loaded page — so they must keep the
  // segmented list rendering instead of an empty state.
  const hasServerAttentionRows =
    !m.isScopedList &&
    !m.threadSearchActive &&
    (m.attentionWaiting.length > 0 ||
      m.attentionPatientReply.length > 0 ||
      m.attentionBotAck.length > 0);

  // The fully-virtual list serves the normal scope's chronological views ("all" lens, standard
  // and groups) plus the WhatsApp Groups lens (the same window, scoped server-side). Search,
  // the needs lens and the SCOPED lists (internal Chats, Private contact chats) stay on the dense
  // SegmentedThreadList — their data is complete per fetch.
  //
  // This must test isScopedList, not isInternalScope: the virtual list renders skeletons until the
  // window reports a total, and the window is disabled for scoped lists — so a scope that reached
  // here showed placeholder rows forever.
  const virtualListActive = isVirtualThreadListActive({
    isScopedList: m.isScopedList,
    threadSearchActive: m.threadSearchActive,
    pinnedLensActive,
    threadLens: m.threadLens,
    waGroupsLensActive: m.waGroupsLensActive,
  });
  // The Groups lens hides + neutralizes the channel pills (they'd contradict the scope=groups
  // population), so they must not count as "active" there — an empty scoped list should say
  // "No WhatsApp group chats yet", not blame filters the user can't see.
  const anyThreadFilterActive =
    m.threadFilterLockedOnly ||
    m.threadFilterAwaitingReplyOnly ||
    (!m.waGroupsLensActive && (m.threadFilterWhatsappOnly || m.threadFilterAppOnly)) ||
    Boolean(m.threadFilterWhatsappBotId) ||
    Boolean(m.threadFilterLastMessageRange?.from);

  // Groups view: dense leading items (per-group sections) built from the SERVER-COMPLETE grouped
  // fetch, rendered above the virtual ungrouped tail. Same construction as the dense list's
  // groups branch; rows inside a section hide the redundant group chip.
  const groupedLeadingItems = useMemo(() => {
    // The Groups lens has no folder sections (folders belong to the default scope; the hook
    // also skips their fetch there) — its window IS the whole scoped list.
    if (!virtualListActive || m.threadListView !== "groups" || m.waGroupsLensActive) return null;
    const rows = Array.isArray(m.groupedThreadRows) ? m.groupedThreadRows : [];
    const out = [];
    const validGroupIds = new Set(m.threadGroups.map((g) => g.id));
    const byGroupId = new Map();
    for (const g of m.threadGroups) byGroupId.set(g.id, []);
    for (const thread of rows) {
      const gid = getThreadGroupId(thread);
      // Unknown/retired group ids belong to the ungrouped tail server-side; skip defensively.
      if (gid && validGroupIds.has(gid)) byGroupId.get(gid).push(thread);
    }
    for (const group of m.threadGroups) {
      const groupThreads = byGroupId.get(group.id) || [];
      if (groupThreads.length === 0) continue;
      const color = getGroupColor(group.colorId);
      const sectionKey = `group:${group.id}`;
      const groupCollapsed = m.collapsedSections.has(sectionKey);
      out.push({
        type: "header",
        key: `hdr:${sectionKey}`,
        node: (
          <div className="border-y border-border/40">
            <SectionHeader
              sectionKey={sectionKey}
              collapsed={groupCollapsed}
              onToggle={m.toggleSectionCollapsed}
              className={cn("py-1.5", color.sectionClassName)}
              rightSlot={
                <span className="font-mono text-[11px] normal-case opacity-80">{groupThreads.length}</span>
              }
            >
              <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", color.dotClassName)} />
              <span className="truncate">{group.name}</span>
              {groupHasFlag(group, THREAD_GROUP_FLAG_IGNORE) ? (
                <BotOff className="h-3 w-3 shrink-0 opacity-70" title="Bot ignores this group" aria-hidden />
              ) : null}
            </SectionHeader>
          </div>
        ),
      });
      if (!groupCollapsed) {
        for (const thread of groupThreads) {
          out.push({
            type: "row",
            // `lead:` namespace — the sparse tail keys rows `row:<id>`, and during a
            // stale-while-revalidate beat the same thread can transiently exist in both regions.
            key: `lead:${getThreadId(thread)}`,
            thread,
            extra: { hideGroupChip: true },
          });
        }
      }
    }
    if (out.length && m.groupedThreadsTruncated) {
      out.push({
        type: "header",
        key: "hdr:grouped-truncated",
        node: (
          <div className="px-3 py-1.5 text-[10px] text-muted-foreground">
            Showing the first {rows.length} grouped threads
          </div>
        ),
      });
    }
    // "All threads" section header above the virtual ungrouped tail, mirroring the dense view —
    // including its collapse toggle (VirtualThreadList hides the sparse region while collapsed).
    out.push({
      type: "header",
      key: "hdr:all",
      node: (
        <SectionHeader
          sectionKey="all"
          collapsed={m.collapsedSections.has("all")}
          onToggle={m.toggleSectionCollapsed}
          className="text-muted-foreground"
        >
          <span>All threads{m.threadListWindow.total != null ? ` · ${m.threadListWindow.total}` : ""}</span>
        </SectionHeader>
      ),
    });
    return out;
  }, [
    virtualListActive,
    m.threadListView,
    m.waGroupsLensActive,
    m.groupedThreadRows,
    m.groupedThreadsTruncated,
    m.threadGroups,
    m.collapsedSections,
    m.toggleSectionCollapsed,
    m.threadListWindow.total,
  ]);
  const groupsTailCollapsed =
    virtualListActive &&
    m.threadListView === "groups" &&
    !m.waGroupsLensActive &&
    m.collapsedSections.has("all");

  useLayoutEffect(() => {
    // Virtual mode renders rows from the sparse window (filteredSortedThreads stays empty);
    // skeleton placeholders carry no [data-thread-item], so the stagger only ever touches real
    // rows in either mode.
    const hasRows = virtualListActive
      ? (m.threadListWindow.total ?? 0) > 0
      : m.filteredSortedThreads.length > 0;
    if (!threadListRef.current || !hasRows) return;
    const isFilterChange = filterKey !== prevFilterKeyRef.current;
    prevFilterKeyRef.current = filterKey;
    if (!isFilterChange && initialThreadAnimDone.current) return;
    initialThreadAnimDone.current = true;
    const items = threadListRef.current.querySelectorAll("[data-thread-item]");
    if (items.length === 0) return;

    const viewport = threadListViewportRef.current;
    if (viewport) viewport.style.overflowX = "clip";
    threadListRef.current.style.overflow = "clip";

    // Opacity-only stagger: the rows are virtualized and carry an inline `transform: translateY(...)`
    // for positioning, so animating translateX here would clobber that and stack rows at the top.
    // A fade-in keeps the entrance polish without touching the transform the virtualizer owns.
    animate(items, {
      opacity: [0, 1],
      duration: 280,
      delay: stagger(12),
      ease: "outCubic",
    });

    // The viewport is put back to "hidden" rather than to "": Radix writes that overflow inline
    // and React holds "hidden" as its last written value, so clearing the property is never
    // repaired — the viewport would stay overflow-x visible (i.e. auto beside a scrolling y
    // axis) and scroll sideways for the rest of the session. This ScrollArea is vertical-only,
    // so "hidden" is exactly what Radix set.
    const totalMs = 280 + 12 * Math.max(0, items.length - 1) + 40;
    const timer = setTimeout(() => {
      if (viewport) viewport.style.overflowX = "hidden";
      if (threadListRef.current) threadListRef.current.style.overflow = "";
    }, totalMs);
    return () => {
      clearTimeout(timer);
      if (viewport) viewport.style.overflowX = "hidden";
      if (threadListRef.current) threadListRef.current.style.overflow = "";
    };
  }, [filterKey, m.filteredSortedThreads.length, virtualListActive, m.threadListWindow.generation]);

  // Patient Messages hoists its thread-list toolbar — search and "New message" — up into the
  // shared app header (rendered via a portal), so the thread column starts right under the top
  // bar. The result counter sits in the lens row beside the filter toggle (the titlebar is
  // absent in web mode, so nothing page-critical may live there). Page size is a Settings →
  // Preferences row; the filter panel toggles from the lens row inside the list. Internal
  // Chats has none of these, so it stays with its own compact panel header below.
  // Normal scope (non-search) counts come from the server's EXACT totals: shown = the active
  // filter's total (groups view adds the server-complete grouped rows above the ungrouped tail),
  // total = the unfiltered inbox total from the overview. Search/internal fall back to loaded
  // lengths (their fetches are complete).
  const useServerTotals =
    !m.isScopedList && !m.threadSearchActive && m.threadListWindow.total != null;
  const groupedShownCount =
    m.threadListView === "groups" && Array.isArray(m.groupedThreadRows)
      ? m.groupedThreadRows.length
      : 0;
  const threadTotalCount = useServerTotals
    ? m.waGroupsLensActive
      ? // The Groups lens is its own population: the overview's unfiltered total counts the
        // DEFAULT scope (which excludes groups), so the window's own scoped total is the only
        // honest "of N" here.
        m.threadListWindow.total
      : (m.threadUnfilteredTotal ?? m.threadListWindow.total + groupedShownCount)
    : m.sortedThreads.length;
  const threadShownCount = useServerTotals
    ? m.threadListWindow.total + groupedShownCount
    : m.filteredSortedThreads.length;
  const threadCountNarrowed = threadShownCount !== threadTotalCount;
  const headerToolbar = m.isScopedList ? null : (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <ThreadWhatsAppNumberControl
        whatsappNumbers={m.whatsappNumbers}
        sectionBotId={m.threadFilterWhatsappBotId}
        onChange={m.setThreadFilterWhatsappBotId}
      />
      <div className="relative ml-auto min-w-0 max-w-sm flex-1">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <input
          type="text"
          value={m.threadSearchQuery}
          onChange={(e) => m.setThreadSearchQuery(e.target.value)}
          placeholder="Search name, phone, or message…"
          aria-label="Search threads by name, phone, or message"
          className="h-8 w-full rounded-lg border border-border bg-background pl-8 pr-8 text-xs shadow-sm transition placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        {m.threadSearchQuery ? (
          <button
            type="button"
            onClick={() => m.setThreadSearchQuery("")}
            aria-label="Clear search"
            className="absolute right-1.5 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
      {canManage && (
        <Button size="sm" className="shrink-0" onClick={() => setNewPatientMessageOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          New message
        </Button>
      )}
      {/* <SegmentedToggle
        value={m.viewMode}
        onChange={m.setViewMode}
        ariaLabel="Messages view"
        size="sm"
        segments={[
          { id: "list", label: "List", icon: List, title: "Thread list" },
          { id: "board", label: "Board", icon: KanbanSquare, title: "Triage board — columns by status" },
        ]}
      /> */}
    </div>
  );

  // Thread counter, shown at the right of the lens row (via ThreadFilterBar's resultCount).
  const threadCounter = m.isScopedList ? null : (
    <span
      className="select-none whitespace-nowrap text-[11px] tabular-nums text-muted-foreground"
      aria-live="polite"
      title={`${threadShownCount} of ${threadTotalCount} threads shown`}
    >
      {threadCountNarrowed ? (
        <>
          <span className="font-semibold text-foreground">{threadShownCount}</span>
          <span className="text-muted-foreground/70"> / {threadTotalCount}</span>
        </>
      ) : (
        <>
          <span className="font-semibold text-foreground">{threadTotalCount}</span> threads
        </>
      )}
    </span>
  );

  // DISABLED (for now): triage board view — always use the thread list below.
  // if (!m.isInternalScope && m.viewMode === "board") { ... }

  return (
    <div className="h-full w-full">
      {headerSlotEl && headerToolbar ? createPortal(headerToolbar, headerSlotEl) : null}
      <ResizablePanelGroup orientation="horizontal" className="h-full w-full border-b">
        <ResizablePanel defaultSize="400px" minSize="200px" maxSize="600px">
          <div className="flex h-full min-h-0 flex-col">
            {m.threadFlagsError ? (
              <div className="shrink-0 border-b border-destructive/30 bg-destructive/5 px-4 py-2 text-xs text-destructive">
                {m.threadFlagsError}
              </div>
            ) : null}
            {m.threadGroupsError ? (
              <div className="shrink-0 border-b border-destructive/30 bg-destructive/5 px-4 py-2 text-xs text-destructive">
                {m.threadGroupsError}
              </div>
            ) : null}
            {m.isScopedList ? (
              <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-2.5">
                <span className="text-sm font-semibold">
                  {m.isDirectScope ? "Private" : "Chats"}
                </span>
                {/* No "New chat" in Private: these conversations are started from the phone's
                    address book, which the desktop does not have. Read and reply here. */}
                {canManage && !m.isDirectScope && (
                  <Button size="sm" onClick={() => setNewChatOpen(true)}>
                    <Plus className="mr-1 h-4 w-4" />
                    New chat
                  </Button>
                )}
              </div>
            ) : (
              <ThreadFilterBar
                threads={m.sortedThreads}
                searchQuery={m.threadSearchQuery}
                showLens
                lens={m.threadLens}
                onLensChange={m.setThreadLens}
                needsCount={m.needsCount}
                pinnedCount={pinnedCount}
                waGroupsCount={m.waGroupsUnreadCount}
                resultCount={threadCounter}
                // DISABLED (for now): the "Mine" lens, staff_handles + assignee filters.
                // mineCount={m.mineCount}
                onToggleFilters={toggleFiltersCollapsed}
                lockedOnly={m.threadFilterLockedOnly}
                onLockedOnlyChange={m.setThreadFilterLockedOnly}
                // staffHandlesOnly={m.threadFilterStaffHandlesOnly}
                // onStaffHandlesOnlyChange={m.setThreadFilterStaffHandlesOnly}
                awaitingReplyOnly={m.threadFilterAwaitingReplyOnly}
                onAwaitingReplyOnlyChange={m.setThreadFilterAwaitingReplyOnly}
                whatsappOnly={m.threadFilterWhatsappOnly}
                onWhatsappOnlyChange={m.setThreadFilterWhatsappOnly}
                appOnly={m.threadFilterAppOnly}
                onAppOnlyChange={m.setThreadFilterAppOnly}
                // assignedToMe={m.threadFilterAssignedToMe}
                // onAssignedToMeChange={m.setThreadFilterAssignedToMe}
                // assigneeId={m.threadFilterAssigneeId}
                // onAssigneeIdChange={m.setThreadFilterAssigneeId}
                whatsappNumbers={m.whatsappNumbers}
                sectionBotId={m.threadFilterWhatsappBotId}
                onSectionBotIdChange={m.setThreadFilterWhatsappBotId}
                lastMessageRange={m.threadFilterLastMessageRange}
                onLastMessageRangeChange={m.setThreadFilterLastMessageRange}
                onClearFilters={m.clearThreadFilters}
                threadListView={m.threadListView}
                onThreadListViewChange={m.setThreadListView}
                onCollapseAll={m.collapseAllSections}
                onExpandAll={m.expandAllSections}
                collapsed={filtersCollapsed}
              />
            )}
            {m.isInternalScope ? (
              <NewChatDialog
                open={newChatOpen}
                onOpenChange={setNewChatOpen}
                onCreate={m.createInternalChat}
                existingDirectPartnerIds={existingDirectPartnerIds}
              />
            ) : (
              <NewPatientMessageDialog
                open={newPatientMessageOpen}
                onOpenChange={setNewPatientMessageOpen}
                onStart={m.startPatientMessage}
                whatsappNumbers={m.whatsappNumbers}
              />
            )}
            <ForwardMessageDialog
              open={Boolean(forwardSource)}
              onOpenChange={(nextOpen) => {
                if (!nextOpen) setForwardSource(null);
              }}
              source={forwardSource}
              onSent={() => setForwardSelectionResetToken((token) => token + 1)}
              whatsappNumbers={m.whatsappNumbers}
            />
            <ScrollArea
              type={threadListHasScrollbar ? "always" : "hover"}
              viewportRef={threadListViewportRef}
              className="min-h-0 flex-1"
            >
              <div className={`flex min-h-full flex-col ${threadListHasScrollbar ? "pr-2" : ""}`}>
                {pinnedLensActive ? (
                  <PinnedMessagesList scope="normal" onSelectPin={handleSelectPin} />
                ) : virtualListActive ? (
                  m.threadListWindow.total === 0 && (groupedLeadingItems?.length ?? 0) === 0 ? (
                    <div className="flex flex-1 items-center justify-center px-4 py-12 text-center text-sm text-muted-foreground">
                      {anyThreadFilterActive
                        ? "No threads match the selected filters"
                        : m.waGroupsLensActive
                          ? "No WhatsApp group chats yet"
                          : "No chats yet"}
                    </div>
                  ) : (
                    <div ref={threadListRef}>
                      <VirtualThreadList
                        window={m.threadListWindow}
                        leadingItems={m.threadListView === "groups" ? groupedLeadingItems : null}
                        tailCollapsed={groupsTailCollapsed}
                        scrollElementRef={threadListViewportRef}
                        listClockMs={m.listClockMs}
                        threadMemberIdsByThreadId={m.threadMemberIdsByThreadId}
                        selectedThreadId={m.selectedThreadId}
                        localUnreadByThreadId={m.localUnreadByThreadId}
                        onSelectThread={m.handleThreadClickStable}
                        onThreadFlagChange={m.handleThreadFlagCheckedChange}
                        groups={m.threadGroups}
                        onThreadGroupChange={m.handleThreadGroupChange}
                        onPatientTagToggle={m.handlePatientTagToggle}
                        onStatusChange={m.handleStatusChange}
                        onDeleteThread={m.requestDeleteThread}
                        showWhatsappNumber={m.whatsappNumbers.length > 1 && !m.threadFilterWhatsappBotId}
                      />
                    </div>
                  )
                ) : m.threadSearchActive && m.threadSearchLoading && m.filteredSortedThreads.length === 0 ? (
                  <div className="flex flex-1 items-center justify-center px-4 py-12 text-sm text-muted-foreground">
                    Searching…
                  </div>
                ) : m.sortedThreads.length > 0 || hasServerAttentionRows ? (
                  m.filteredSortedThreads.length > 0 || hasServerAttentionRows ? (
                    <div ref={threadListRef}>
                      <SegmentedThreadList
                        scrollElementRef={threadListViewportRef}
                        threads={m.filteredSortedThreads}
                        lens={m.threadSearchActive || m.isScopedList ? "all" : m.threadLens}
                        serverWaiting={m.threadSearchActive || m.isScopedList ? null : m.attentionWaiting}
                        serverPatientReply={
                          m.threadSearchActive || m.isScopedList ? null : m.attentionPatientReply
                        }
                        serverBotAck={m.threadSearchActive || m.isScopedList ? null : m.attentionBotAck}
                        listClockMs={m.listClockMs}
                        botAckThresholdMinutes={m.botAckThresholdMinutes}
                        botAckMaxHours={m.botAckMaxHours}
                        patientReplyThresholdMinutes={m.patientReplyThresholdMinutes}
                        patientReplyMaxHours={m.patientReplyMaxHours}
                        // Only meaningful for the server-backed attention lists — a search or
                        // internal-scope list is built from loaded rows, not the capped scan.
                        attentionTruncated={
                          m.threadSearchActive || m.isScopedList ? false : m.attentionTruncated
                        }
                        threadMemberIdsByThreadId={m.threadMemberIdsByThreadId}
                        selectedThreadId={m.selectedThreadId}
                        localUnreadByThreadId={m.localUnreadByThreadId}
                        onSelectThread={m.handleThreadClickStable}
                        onThreadFlagChange={m.handleThreadFlagCheckedChange}
                        groups={m.threadGroups}
                        threadListView={m.threadListView}
                        onThreadGroupChange={m.handleThreadGroupChange}
                        // DISABLED (for now): thread claim — assignment lives on tasks only.
                        // onClaimThread={m.handleClaimThread}
                        onPatientTagToggle={m.handlePatientTagToggle}
                        onStatusChange={m.handleStatusChange}
                        onDeleteThread={m.requestDeleteThread}
                        onMarkThreadRead={m.isScopedList ? m.handleMarkThreadRead : undefined}
                        onMarkThreadUnread={m.isScopedList ? m.handleMarkThreadUnread : undefined}
                        onToggleMute={m.isScopedList ? m.handleToggleChatMute : undefined}
                        mutedThreadIds={m.isScopedList ? m.mutedChatThreadIds : null}
                        onLeaveThread={
                          m.isInternalScope
                            ? (threadId) => {
                                const confirmed = window.confirm(
                                  "Leave this group? You'll stop receiving its messages.",
                                );
                                if (!confirmed) return;
                                void m.handleLeaveGroup(threadId);
                              }
                            : undefined
                        }
                        onDeleteChat={
                          m.isScopedList
                            ? (threadId) => {
                                const confirmed = window.confirm(
                                  "Delete this chat? It will be removed from your Chats.",
                                );
                                if (!confirmed) return;
                                void m.handleDeleteChat(threadId);
                              }
                            : undefined
                        }
                        collapsedSections={m.collapsedSections}
                        onToggleSection={m.toggleSectionCollapsed}
                        onlineUserIds={m.isInternalScope ? m.onlineUserIds : null}
                        availabilityByUserId={m.isInternalScope ? m.availabilityByUserId : null}
                        typingThreadIds={m.isInternalScope ? m.typingThreadIds : null}
                        isInternalScope={m.isScopedList}
                        showWhatsappNumber={m.whatsappNumbers.length > 1 && !m.threadFilterWhatsappBotId}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-1 items-center justify-center px-4 py-12 text-center text-sm text-muted-foreground">
                      No threads match the selected filters
                    </div>
                  )
                ) : (
                  <div className="flex flex-1 items-center justify-center px-4 py-12 text-center text-sm text-muted-foreground">
                    {m.threadSearchActive
                      ? "No threads match your search"
                      : m.isDirectScope && m.directChatDisabled
                        ? "Private chats aren't enabled on this server yet"
                        : "No chats yet"}
                  </div>
                )}
                {m.threadSearchActive && m.threadSearchTruncated ? (
                  <div className="px-4 py-2 text-center text-[11px] text-muted-foreground">
                    {/* Pre-filter count: sortedThreads IS the server's capped result set while
                        searching; the filter bar may narrow what's listed above. */}
                    Showing the first {m.sortedThreads.length} matches — narrow the search to see
                    more.
                  </div>
                ) : null}
              </div>
            </ScrollArea>
          </div>
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize="75%">
          <div className="flex h-full flex-col">
            {!m.selectedThreadId ? (
              <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground">
                Select a thread to see conversation history
              </div>
            ) : (
              <ResizablePanelGroup orientation="horizontal" className="min-h-0 overflow-hidden">
                <ResizablePanel className="min-w-0">
                <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
                  {!m.isScopedList ? (
                    <AiHandlingBanner
                      thread={m.selectedThreadForHeader}
                      threadTasks={m.threadTasks}
                      onThreadFlagChange={m.handleThreadFlagCheckedChange}
                      onAcknowledgeMessage={m.handleAcknowledgeMessage}
                    />
                  ) : null}
                  {String(m.selectedThreadForHeader?.lastMessageId || "").trim() &&
                  getPatientReplyWaitingMinutes(
                    m.selectedThreadForHeader,
                    m.patientReplyThresholdMinutes,
                    m.patientReplyMaxHours,
                    m.listClockMs,
                  ) != null ? (
                    <div
                      role="status"
                      aria-live="polite"
                      className="flex shrink-0 items-center gap-2.5 border-b border-rose-300/50 bg-gradient-to-r from-rose-100/80 via-rose-50/70 to-rose-100/80 px-4 py-2 text-rose-950 dark:border-rose-500/35 dark:from-rose-950/45 dark:via-rose-900/30 dark:to-rose-950/45 dark:text-rose-50"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-rose-500/15 ring-1 ring-rose-500/30 dark:bg-rose-500/20 dark:ring-rose-400/40">
                        <UserRound className="h-3.5 w-3.5 text-rose-700 dark:text-rose-300" strokeWidth={2.5} aria-hidden />
                      </span>
                      <div className="min-w-0 flex-1 leading-tight">
                        <p className="text-xs font-semibold tracking-tight">Unanswered patient reply</p>
                        <p className="text-[10px] text-rose-800/80 dark:text-rose-200/70">
                          The patient sent the last message and it hasn’t been answered. Acknowledge to clear it from “Unanswered patient reply”.
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const tid = String(
                            m.selectedThreadForHeader?.threadId || m.selectedThreadId || "",
                          ).trim();
                          const mid = String(m.selectedThreadForHeader?.lastMessageId || "").trim();
                          if (!tid || !mid) return;
                          void m.handleAcknowledgeMessage(tid, mid);
                        }}
                        className="h-7 shrink-0 border-rose-400/60 bg-background/70 px-2.5 text-[11px] font-semibold text-rose-900 shadow-sm transition hover:border-rose-500/80 hover:bg-rose-200/70 hover:text-rose-950 dark:border-rose-400/50 dark:bg-rose-950/40 dark:text-rose-100 dark:hover:bg-rose-900/60"
                      >
                        <Check className="mr-1 h-3 w-3" strokeWidth={2.5} aria-hidden />
                        Acknowledge
                      </Button>
                    </div>
                  ) : null}
                  {String(m.selectedThreadForHeader?.lastMessageId || "").trim() &&
                  getBotAckWaitingMinutes(
                    m.selectedThreadForHeader,
                    m.botAckThresholdMinutes,
                    m.botAckMaxHours,
                    m.listClockMs,
                  ) != null ? (
                    <div
                      role="status"
                      aria-live="polite"
                      className="flex shrink-0 items-center gap-2.5 border-b border-amber-300/50 bg-gradient-to-r from-amber-100/80 via-amber-50/70 to-amber-100/80 px-4 py-2 text-amber-950 dark:border-amber-500/35 dark:from-amber-950/45 dark:via-amber-900/30 dark:to-amber-950/45 dark:text-amber-50"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber-500/15 ring-1 ring-amber-500/30 dark:bg-amber-500/20 dark:ring-amber-400/40">
                        <Bot className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" strokeWidth={2.5} aria-hidden />
                      </span>
                      <div className="min-w-0 flex-1 leading-tight">
                        <p className="text-xs font-semibold tracking-tight">Unanswered bot reply</p>
                        <p className="text-[10px] text-amber-800/80 dark:text-amber-200/70">
                          The assistant sent the last message. Acknowledge to clear it from “Unanswered bot replies”.
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const tid = String(
                            m.selectedThreadForHeader?.threadId || m.selectedThreadId || "",
                          ).trim();
                          const mid = String(m.selectedThreadForHeader?.lastMessageId || "").trim();
                          if (!tid || !mid) return;
                          void m.handleAcknowledgeMessage(tid, mid);
                        }}
                        className="h-7 shrink-0 border-amber-400/60 bg-background/70 px-2.5 text-[11px] font-semibold text-amber-900 shadow-sm transition hover:border-amber-500/80 hover:bg-amber-200/70 hover:text-amber-950 dark:border-amber-400/50 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-900/60"
                      >
                        <Check className="mr-1 h-3 w-3" strokeWidth={2.5} aria-hidden />
                        Acknowledge
                      </Button>
                    </div>
                  ) : null}
                  <div className="border-b border-border/60 px-4 py-2">
                    {/* Row 1 — identity + actions; Row 2 below spans the full header width. */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onDoubleClick={() => {
                          if (headerPhotoUrl) {
                            setHeaderPhotoModalOpen(true);
                          }
                        }}
                        className="shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        aria-label={headerPhotoUrl ? "Open profile photo preview" : "Generated profile avatar"}
                        title={headerPhotoUrl ? "Double-click to enlarge" : "Generated avatar"}
                      >
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={m.selectedThreadForHeader?.avatarUrl} />
                          <AvatarFallback className={cn("text-[10px] font-semibold", headerAvatarColorClasses)}>
                            {headerIsWaGroup ? (
                              <Users className="h-4 w-4" strokeWidth={2.25} aria-hidden />
                            ) : (
                              headerAvatarInitials
                            )}
                          </AvatarFallback>
                        </Avatar>
                      </button>
                      <div className="flex min-w-0 flex-1 items-baseline gap-2.5">
                        <h2
                          className={`flex min-w-0 items-center gap-2 text-[15px] font-semibold leading-tight ${m.headerThreadPlaceholder ? "text-muted-foreground" : "text-foreground"}`}
                        >
                          <EditableConversationTitle
                            title={m.conversationHeaderTitle}
                            secondary={m.conversationHeaderSecondary}
                            isPlaceholder={m.headerThreadPlaceholder}
                            canEdit={m.headerIsInternalGroup || (!m.isScopedList && Boolean(m.headerRenameTarget))}
                            editHint={m.headerIsInternalGroup ? "Rename group" : undefined}
                            onSave={(name) =>
                              m.headerIsInternalGroup
                                ? m.handleRenameGroup(name)
                                : m.handleRenameThreadContact(m.headerRenameTarget?.userId, name)
                            }
                          >
                            {headerShowOnlineDot ? (
                              <PresenceDot
                                online
                                availability={m.availabilityByUserId?.[headerOtherMemberId]}
                              />
                            ) : null}
                          </EditableConversationTitle>
                        </h2>
                        <div className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                          {/* In the merged view the open thread's count would undercount the
                              interleaved multi-clinic stream — the "N WhatsApp threads" subline says
                              it instead. */}
                          {headerTypingLabel ? (
                            <span className="animate-pulse whitespace-nowrap font-medium text-primary">
                              {headerTypingLabel}
                            </span>
                          ) : headerIsWaGroup && headerWaGroupParticipantCount > 0 ? (
                            // Group conversations lead with the roster, not the message count —
                            // and the subtitle doubles as the shortcut into the Participants tab.
                            <button
                              type="button"
                              title="View participants"
                              onClick={() => {
                                m.setRightPanelMode("participants");
                                m.setRightPanelOpen(true);
                              }}
                              className="inline-flex items-center gap-1 whitespace-nowrap rounded transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <Users className="h-3 w-3 shrink-0" strokeWidth={2.25} aria-hidden />
                              {headerWaGroupParticipantCount} participant
                              {headerWaGroupParticipantCount === 1 ? "" : "s"}
                            </button>
                          ) : !m.mergedActive ? (
                            <span className="whitespace-nowrap">
                              {m.historyLoading
                                ? "Loading messages..."
                                : `${m.historyItems.length} message${m.historyItems.length === 1 ? "" : "s"}`}
                              {/* {import.meta.env.DEV && ` [(threadId) ${m.selectedThreadForHeader?.threadId}]`} */}
                            </span>
                          ) : null}
                          {/* Search this conversation. The keyboard route (Cmd/Ctrl+F) is the
                              fast one; this makes the feature discoverable. */}
                          {m.selectedThreadId ? (
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    aria-label="Search this conversation"
                                    aria-pressed={chatSearchOpen}
                                    onClick={() =>
                                      chatSearchOpen
                                        ? closeChatSearch()
                                        : openChatSearch(CHAT_SEARCH_SCOPE_THREAD)
                                    }
                                    className={cn(
                                      "inline-flex h-6 w-6 items-center justify-center rounded border transition",
                                      chatSearchOpen
                                        ? "border-indigo-500/60 bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"
                                        : "border-border/70 text-muted-foreground hover:bg-muted hover:text-foreground",
                                    )}
                                  >
                                    <Search className="h-3.5 w-3.5" aria-hidden />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="text-xs">
                                  Search messages ({navigator.platform?.startsWith("Mac") ? "⌘" : "Ctrl+"}F)
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : null}
                          {/* OLD: {import.meta.env.DEV ? (thread action controls) : null} */}
                          {/* FIXME: Re-enable DEV-only guard for these destructive/admin controls after rollout validation. */}
                          {/* Clear-thread + reset-session are patient-triage/bot admin actions; they make no sense for staff-to-staff chats. */}
                          {m.isScopedList ? (
                            <TooltipProvider delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    aria-label="Delete this chat"
                                    onClick={() => {
                                      const threadId = String(m.selectedThreadForHeader?.threadId || "").trim();
                                      if (!threadId) return;
                                      const confirmed = window.confirm(
                                        "Delete this chat? It will be removed from your Chats.",
                                      );
                                      if (!confirmed) return;
                                      void m.handleDeleteChat(threadId);
                                    }}
                                    disabled={m.deleteChatInProgress}
                                    className="inline-flex h-6 items-center gap-1 rounded border border-destructive/40 px-1.5 text-xs font-medium text-destructive transition hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                                    Delete chat
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="text-xs">
                                  Delete chat
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : null}
                          {!m.isScopedList ? (
                          <TooltipProvider delayDuration={150}>
                            <div className="inline-flex items-center gap-1">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    aria-label="Clear all messages in this thread"
                                    onClick={() => {
                                      const threadId = String(m.selectedThreadForHeader?.threadId || "").trim();
                                      if (!threadId) return;
                                      const confirmed = window.confirm(
                                        `Delete all messages in thread ${threadId}? This cannot be undone.`,
                                      );
                                      if (!confirmed) return;
                                      void m.handleClearSelectedThreadMessages();
                                    }}
                                    disabled={m.clearThreadMessagesInProgress || m.historyLoading}
                                    className={cn(
                                      "inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground/70 transition hover:bg-destructive/10 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-50",
                                    )}
                                  >
                                    <Trash2 className="h-3 w-3" aria-hidden />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="text-xs">
                                  Clear thread messages
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    aria-label="Reset flow session only (clear Redis triage/bot state, keep messages)"
                                    onClick={() => {
                                      const threadId = String(m.selectedThreadForHeader?.threadId || "").trim();
                                      if (!threadId) return;
                                      const confirmed = window.confirm(
                                        `Reset triage/bot session state for thread ${threadId}? Messages will stay in the thread.`,
                                      );
                                      if (!confirmed) return;
                                      void m.handleResetSelectedThreadSession();
                                    }}
                                    disabled={m.clearThreadMessagesInProgress || m.historyLoading}
                                    className={cn(
                                      "inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground/70 transition hover:bg-amber-500/10 hover:text-amber-600 dark:hover:text-amber-300 disabled:cursor-not-allowed disabled:opacity-50",
                                    )}
                                  >
                                    <RotateCcw className="h-3 w-3" strokeWidth={2.25} aria-hidden />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="max-w-[220px] text-xs">
                                  Reset session only — clears triage/flow Redis keys; does not delete messages
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </TooltipProvider>
                          ) : null}
                          {showArchiveAffordances ? (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  aria-label="Load archived messages"
                                  onClick={() => m.triggerArchiveLoad()}
                                  disabled={m.historyLoading}
                                  className={cn(
                                    "inline-flex h-5 w-5 items-center justify-center rounded transition disabled:cursor-not-allowed disabled:opacity-50",
                                    m.archiveTriggered
                                      ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                                      : "text-muted-foreground/70 hover:bg-amber-500/10 hover:text-amber-600 dark:hover:text-amber-300",
                                  )}
                                >
                                  <Archive className="h-3 w-3" strokeWidth={2} aria-hidden />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="text-xs">
                                Load archived messages
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          ) : null}
                        </div>
                      </div>
                      {headerIsTriageScope ? (
                        // One quiet h-5 row matching the metadata chips below — currently just
                        // the Tasks toggle; the status chip and Snooze/Done quick actions are
                        // DISABLED (for now) inside.
                        <div className="flex shrink-0 items-center gap-1.5">
                          {/* DISABLED (for now): the status chip + Snooze quick action — in
                              the current logic the workflow status is derived automatically,
                              so the header keeps only the Tasks toggle.
                          {headerStatusDef ? (
                            <span
                              className={cn(
                                "inline-flex h-5 items-center gap-1 rounded-full px-2 text-[10.5px] font-semibold leading-none",
                                headerStatusDef.chipClassName,
                              )}
                              title={`Workflow status: ${headerStatusDef.label}`}
                            >
                              <headerStatusDef.icon className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} aria-hidden />
                              {headerStatusDef.label}
                              DISABLED (for now): the "· owner" suffix — the "assigned"
                                  status no longer derives (thread assignment is hidden).
                              {headerEffectiveStatus === "assigned" && headerAssignee
                                ? ` · ${headerAssigneeIsMe ? "You" : headerAssignee.name}`
                                : ""}
                            </span>
                          ) : null}
                          <SnoozeMenu
                            onSnooze={(until) => {
                              if (headerThreadId) void m.handleSnoozeThread(headerThreadId, until);
                            }}
                          >
                            <button
                              type="button"
                              title="Snooze — moves to Pending and leaves your queue"
                              className="inline-flex h-5 items-center gap-1 rounded-full border border-border bg-background px-2 text-[10.5px] font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <Clock className="h-2.5 w-2.5" strokeWidth={2.25} aria-hidden />
                              Snooze
                            </button>
                          </SnoozeMenu>
                          */}
                          <button
                            type="button"
                            onClick={toggleTasksPanel}
                            aria-pressed={tasksPanelShowing}
                            title="Tasks panel"
                            className={cn(
                              "inline-flex h-5 items-center gap-1 rounded-full border px-2 text-[10.5px] font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                              tasksPanelShowing
                                ? "border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-500/40 dark:bg-indigo-950/60 dark:text-indigo-300"
                                : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground",
                            )}
                          >
                            <ListChecks className="h-2.5 w-2.5" strokeWidth={2.25} aria-hidden />
                            Tasks
                            {headerOpenTaskCount > 0 ? (
                              <span className="inline-flex h-3 min-w-3 items-center justify-center rounded-full bg-indigo-600 px-[3px] text-[9px] font-bold leading-none text-white dark:bg-indigo-500">
                                {headerOpenTaskCount}
                              </span>
                            ) : null}
                          </button>
                          {/* DISABLED (for now): the Done / Reopen quick action — hidden with
                              the status chip; status changes stay derived (or via the board).
                          {headerEffectiveStatus === "done" ? (
                            <button
                              type="button"
                              onClick={() => {
                                if (headerThreadId) void m.handleStatusChange(headerThreadId, "");
                              }}
                              title="Reopen this conversation"
                              className="inline-flex h-5 items-center gap-1 rounded-full border border-emerald-300 bg-background px-2 text-[10.5px] font-medium text-emerald-700 transition hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-emerald-500/40 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
                            >
                              <RotateCcw className="h-2.5 w-2.5" strokeWidth={2.25} aria-hidden />
                              Reopen
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                if (headerThreadId) void m.handleStatusChange(headerThreadId, "done");
                              }}
                              title="Done — drops out of active lists; re-opens if the patient writes again"
                              className="inline-flex h-5 items-center gap-1 rounded-full bg-emerald-600 px-2.5 text-[10.5px] font-semibold text-white shadow-sm transition hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:bg-emerald-600 dark:hover:bg-emerald-500"
                            >
                              <Check className="h-2.5 w-2.5" strokeWidth={2.5} aria-hidden />
                              Done
                            </button>
                          )}
                          */}
                        </div>
                      ) : null}
                    </div>
                    {!m.isScopedList && m.selectedThreadForHeader ? (
                      <div className="mt-1.5 flex w-full flex-wrap items-center gap-1.5">
                            {m.mergedActive ? (
                              <>
                                <MergedSourcesFilter
                                  sources={m.mergedSourceOptions}
                                  visibleCount={m.mergedVisibleSourceCount}
                                  allVisible={m.mergedAllSourcesVisible}
                                  onToggle={m.onToggleSourceVisibility}
                                  onShowAll={m.onShowAllSources}
                                />
                                {m.mergedHeaderSubline ? (
                                  <span className="text-[10.5px] leading-none text-muted-foreground">
                                    {m.mergedHeaderSubline}
                                  </span>
                                ) : null}
                              </>
                            ) : m.selectedThreadForHeader?.whatsappNumberLabel ? (
                              <span
                                className="inline-flex h-5 items-center gap-1 rounded-full border border-border/70 bg-muted/40 px-2 text-[10.5px] font-medium leading-none text-foreground/75"
                                title={`This conversation is on ${m.selectedThreadForHeader.whatsappNumberLabel}. Replies go out from this number.`}
                              >
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500" aria-hidden />
                                {m.selectedThreadForHeader.whatsappNumberLabel}
                              </span>
                            ) : null}
                            {/* DISABLED (for now): the header assignee chip + "Assign this
                                conversation" picker — thread assignment is hidden; assignment
                                lives on tasks only (see the Tasks panel per-task picker).
                            {(() => {
                              const assignee = getThreadAssignee(m.selectedThreadForHeader);
                              return assignee ? (
                                <AssigneeChip assignee={assignee} className="h-5 rounded-full pr-2" />
                              ) : (
                                <span className="inline-flex h-5 items-center rounded-full border border-dashed border-border px-2 text-[10.5px] leading-none text-muted-foreground">
                                  Unassigned
                                </span>
                              );
                            })()}
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  aria-label="Assign this conversation"
                                  title="Assign this conversation"
                                  className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-border text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
                                >
                                  <UserCog className="h-3 w-3" aria-hidden />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="start" className="w-64 p-1.5">
                                <div className="px-1.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                  Assign to
                                </div>
                                <div className="max-h-64 overflow-y-auto">
                                  {(m.threadMembersGrouped?.team || []).length === 0 ? (
                                    <p className="px-1.5 py-2 text-xs text-muted-foreground">
                                      No team members on this thread.
                                    </p>
                                  ) : (
                                    (m.threadMembersGrouped?.team || []).map((member) => {
                                      const tid = String(
                                        m.selectedThreadForHeader?.threadId || m.selectedThreadId || "",
                                      ).trim();
                                      const current = getThreadAssignee(m.selectedThreadForHeader);
                                      const active = current?.id === member.id;
                                      return (
                                        <button
                                          key={member.key || member.id}
                                          type="button"
                                          onClick={() => {
                                            if (tid && member.id) void m.handleAssignThread(tid, member.id);
                                          }}
                                          className={cn(
                                            "flex w-full items-center gap-2 rounded-md px-1.5 py-1.5 text-left text-xs transition hover:bg-muted/70",
                                            active ? "bg-muted" : "",
                                          )}
                                        >
                                          <Avatar className="h-5 w-5">
                                            <AvatarImage src={member.avatarUrl} />
                                            <AvatarFallback
                                              className={cn(
                                                "text-[9px] font-semibold",
                                                getAvatarColorClasses(member.label || member.id),
                                              )}
                                            >
                                              {getThreadInitials(member.label || member.id)}
                                            </AvatarFallback>
                                          </Avatar>
                                          <span className="min-w-0 flex-1 truncate">{member.label}</span>
                                          {active ? (
                                            <span
                                              className="h-2 w-2 shrink-0 rounded-full bg-sky-500"
                                              aria-hidden
                                            />
                                          ) : null}
                                        </button>
                                      );
                                    })
                                  )}
                                </div>
                                {getThreadAssignee(m.selectedThreadForHeader) ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const tid = String(
                                        m.selectedThreadForHeader?.threadId || m.selectedThreadId || "",
                                      ).trim();
                                      if (tid) void m.handleUnassignThread(tid);
                                    }}
                                    className="mt-1 flex w-full items-center gap-1.5 rounded-md px-1.5 py-1.5 text-left text-xs text-muted-foreground transition hover:bg-muted/70"
                                  >
                                    <X className="h-3.5 w-3.5 shrink-0" aria-hidden /> Clear assignee
                                  </button>
                                ) : null}
                              </PopoverContent>
                            </Popover>
                            */}
                            {headerIsTriageScope && headerPatientUserId ? (
                              <>
                                <span className="h-3 w-px shrink-0 bg-border" aria-hidden />
                                {headerPatientTags.map((key) => {
                                  const def = getPatientTagDefinition(key);
                                  if (!def) return null;
                                  const TagIcon = def.icon;
                                  return (
                                    <span
                                      key={key}
                                      className={cn(
                                        "inline-flex h-5 items-center gap-1 rounded-full px-2 text-[10.5px] font-semibold leading-none",
                                        def.chipClassName,
                                      )}
                                      title={def.description}
                                    >
                                      <TagIcon
                                        className="h-3 w-3 shrink-0"
                                        strokeWidth={2.5}
                                        aria-hidden
                                      />
                                      {def.label}
                                    </span>
                                  );
                                })}
                                <PatientTagPicker
                                  activeTags={headerPatientTags}
                                  onToggle={(key, next) => {
                                    if (headerThreadId) void m.handlePatientTagToggle(headerThreadId, key, next);
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => setPatientEditorOpen(true)}
                                  className="inline-flex h-5 items-center gap-1 rounded-full border border-border/70 bg-muted/40 px-2 text-[10.5px] font-medium leading-none text-foreground/75 transition hover:bg-muted"
                                  title="Edit patient details"
                                >
                                  <Pencil className="h-2.5 w-2.5 shrink-0" aria-hidden /> Edit patient
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setSwitchPatientOpen(true)}
                                  className="inline-flex h-5 items-center gap-1 rounded-full border border-border/70 bg-muted/40 px-2 text-[10.5px] font-medium leading-none text-foreground/75 transition hover:bg-muted"
                                  title="Switch this conversation to a different patient"
                                >
                                  <ArrowRightLeft className="h-2.5 w-2.5 shrink-0" aria-hidden /> Switch
                                </button>
                              </>
                            ) : null}
                      </div>
                    ) : null}
                  </div>
                  {chatSearchOpen ? (
                    <ChatSearchBar
                      inputRef={chatSearch.inputRef}
                      query={chatSearch.query}
                      onQueryChange={chatSearch.setQuery}
                      searchScope={chatSearch.searchScope}
                      onSearchScopeChange={chatSearch.setSearchScope}
                      allChats={chatSearch.allChats}
                      dateRange={chatSearch.dateRange}
                      onDateRangeChange={chatSearch.setDateRange}
                      results={chatSearch.results}
                      total={chatSearch.total}
                      activeIndex={chatSearch.activeIndex}
                      loading={chatSearch.loading}
                      error={chatSearch.error}
                      minTermLength={chatSearch.minTermLength}
                      scopedThreadCount={chatSearch.scopedThreadCount}
                      onStep={chatSearch.step}
                      onClose={closeChatSearch}
                    />
                  ) : null}
                  {/* `relative` anchors the results overlay, which floats ABOVE the scroll
                      viewport instead of shrinking it — the pane's stick-to-bottom, prepend
                      anchoring and late-growth observer all measure that viewport. */}
                  {/* container-type:size (not Tailwind's inline-size-only `@container`) so the
                      results overlay can cap itself at half the PANE with cqh units. The pane's
                      size comes from flex, never from its contents, so size containment is a no-op
                      for layout here. */}
                  <div className="chat-pane-texture relative min-h-0 flex-1 p-0 [container-type:size]">
                    {chatSearchOpen && chatSearch.active ? (
                      <ChatSearchResults
                        term={chatSearch.query.trim()}
                        results={chatSearch.results}
                        threadsById={chatSearch.threadsById}
                        activeIndex={chatSearch.activeIndex}
                        loading={chatSearch.loading}
                        loadingMore={chatSearch.loadingMore}
                        hasMore={chatSearch.hasMore}
                        error={chatSearch.error}
                        truncated={chatSearch.truncated}
                        allChats={chatSearch.allChats}
                        mergedActive={m.mergedActive}
                        getSegmentMeta={m.mergedGetSegmentMeta}
                        getSenderLabel={(row) => m.getSenderLabel?.({ sender: row.sender })}
                        whatsappNumbers={m.whatsappNumbers}
                        onSelect={chatSearch.reveal}
                        onLoadMore={chatSearch.loadMore}
                      />
                    ) : null}
                    {/* While the results panel is up it covers the top half of the pane, so a
                        revealed message centred in the raw viewport would land behind it.
                        scroll-padding shrinks the box scrollIntoView aligns against, dropping the
                        target into the visible half — no change to the scroll code itself. */}
                    <ScrollArea
                      viewportRef={m.chatScrollAreaRef}
                      className="h-full"
                      viewportClassName={chatSearchOpen && chatSearch.active ? "scroll-pt-[50%]" : undefined}
                    >
                      <div ref={m.chatContentRef} className="p-4">
                        {/* Archive trigger button – visible when scrolled to top and archive not yet
                            triggered, and only with archive:view (the fetch needs it server-side) */}
                        {showArchiveAffordances && !m.archiveTriggered && !m.historyLoading && m.historyItems.length > 0 && (
                          <div className="mb-4 flex justify-center">
                            <button
                              type="button"
                              id="load-archived-messages-btn"
                              onClick={() => m.triggerArchiveLoad()}
                              className="flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-50/80 px-4 py-2 text-xs font-semibold text-amber-700 shadow-sm transition hover:bg-amber-100/90 hover:border-amber-400/70 dark:border-amber-400/30 dark:bg-amber-950/40 dark:text-amber-300 dark:hover:bg-amber-900/50"
                            >
                              <Archive className="h-3.5 w-3.5" aria-hidden />
                              Load archived messages
                            </button>
                          </div>
                        )}

                        {/* Archived messages section */}
                        {showArchiveAffordances && m.archiveTriggered && (
                          <ArchivedMessageList
                            archivedMessages={m.archivedMessages}
                            archivedLoading={m.archivedLoading}
                            archivedError={m.archivedError}
                            archivedAllLoaded={m.archivedAllLoaded}
                            onLoadMore={m.loadNextArchivedPage}
                            scrollViewportRef={m.chatScrollAreaRef}
                          />
                        )}

                        {!m.historyLoading && !m.historyAllOlderLoaded && m.historyItems.length > 0 && (
                          <div className="mb-4 flex justify-center">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={m.historyLoadingOlder}
                              onClick={() => m.loadOlderHistory()}
                              className="h-8 px-4 text-xs font-medium"
                            >
                              {m.historyLoadingOlder ? "Loading…" : "Load older messages"}
                            </Button>
                          </div>
                        )}

                        <MessageClusterList
                          historyLoading={m.historyLoading}
                          historyError={m.historyError}
                          historyItemsLength={m.historyItems.length}
                          historyEndRef={m.historyEndRef}
                          messageClusters={m.messageClusters}
                          isOwnMessage={m.isOwnMessage}
                          isBotSender={m.isBotSender}
                          getSenderLabel={m.getSenderLabel}
                          getSenderInitials={m.getSenderInitials}
                          getSenderAvatarUrl={m.getSenderAvatarUrl}
                          selectedThreadId={m.selectedThreadId}
                          patientUserId={headerPatientUserId}
                          onToggleReaction={m.handleToggleBusinessReaction}
                          onForwardMessage={canForwardMessages ? handleForwardMessage : undefined}
                          onForwardMessages={canForwardMessages ? handleForwardMessages : undefined}
                          forwardSelectionResetToken={forwardSelectionResetToken}
                          // Internal: Delete for everyone is the author's own unsend (no manage
                          // right needed — the list offers it on own messages only). WhatsApp keeps
                          // messages:manage, the same right that sends.
                          onRevokeMessage={m.isInternalScope || canManage ? setRevokeTarget : undefined}
                          // Edit needs the composer, which only renders under canManage and never
                          // for an announce-only WhatsApp group (same conditions as the composer
                          // branch below) — no composer, nothing to edit in.
                          onEditMessage={
                            canManage && !(m.activeThreadIsWaGroup && m.activeThreadWaGroupInfo?.announce)
                              ? m.handleBeginEditMessage
                              : undefined
                          }
                          pinnedMessageIds={pinnedMessageIds}
                          onTogglePin={m.isScopedList ? undefined : handleTogglePin}
                          onDeleteMessage={m.handleDeleteMessage}
                          onClearMessagesAfter={m.handleClearMessagesAfter}
                          highlightedMessageId={m.highlightedMessageId}
                          searchHighlightTerm={chatSearchOpen && chatSearch.active ? chatSearch.query.trim() : ""}
                          isInternalScope={m.isScopedList}
                          mergedView={m.mergedActive}
                          getSegmentMeta={m.mergedGetSegmentMeta}
                          newMessagesDividerId={m.isScopedList ? m.newMessagesDividerId : null}
                          mentionNames={m.isInternalScope ? mentionNames : null}
                          recipientIds={m.activeThreadRecipientIds}
                          primaryRecipientIds={m.activeThreadPrimaryRecipientIds}
                          isWhatsAppThread={m.activeThreadIsWhatsApp}
                          memberDirectory={m.memberDirectory}
                          isWaGroupThread={m.activeThreadIsWaGroup}
                          waGroupNamesByJid={m.waGroupNamesByJid}
                        />
                      </div>
                    </ScrollArea>
                  </div>
                  {!canManage ? (
                    <div className="flex shrink-0 items-center justify-center gap-2 border-t border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
                      <Lock className="h-3.5 w-3.5" aria-hidden />
                      View only — your access level doesn&apos;t allow replying here.
                    </div>
                  ) : m.activeThreadIsWaGroup && m.activeThreadWaGroupInfo?.announce ? (
                    // Announce-only ("admins can send") WhatsApp group: WhatsApp would reject the
                    // send anyway (surfacing only as a failed tick), so say it up front instead of
                    // offering a composer that cannot deliver.
                    <div className="flex shrink-0 items-center justify-center gap-2 border-t border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
                      <Lock className="h-3.5 w-3.5" aria-hidden />
                      Only admins can send in this group
                    </div>
                  ) : (
                  <MessageComposerContainer
                    ref={m.composerApiRef}
                    isInternalScope={m.isScopedList}
                    fileInputRef={m.fileInputRef}
                    selectedAttachments={m.selectedAttachments}
                    composerStickerPreviews={m.composerStickerPreviews}
                    composerAttachmentPreviews={m.composerAttachmentPreviews}
                    composerOtherAttachments={m.composerOtherAttachments}
                    chatConnectionStatus={m.chatConnectionStatus}
                    sendInProgress={m.sendInProgress}
                    draftReplyAiModalTriggerDisabled={!m.selectedThreadId || m.sendInProgress}
                    canUseAiRewrite={canUseAiRewrite}
                    onOpenDraftReplyAiModal={m.openDraftReplyAiModal}
                    runQuickRewrite={m.quickRewriteDraftReplyAi}
                    /* Both merges feed this one picker: the patient merge (threads across
                       clinic numbers) and the group merge (one group's per-gateway copies).
                       Every OTHER m.mergedActive consumer stays patient-only. */
                    mergedActive={m.composerReplyPickerActive}
                    mergedSources={m.mergedSources}
                    mergedReplyBotId={m.mergedReplyBotId}
                    mergedReplyIsAuto={m.mergedReplyIsAuto}
                    mergedReplySource={m.mergedReplySource}
                    onSelectReplyFrom={m.onSelectReplyFrom}
                    onAttachmentPick={m.handleAttachmentPick}
                    onRemoveAttachment={m.handleRemoveAttachment}
                    onSend={m.handleSendMessage}
                    onComposerKeyDown={m.handleComposerKeyDown}
                    onComposerDragOver={m.handleComposerDragOver}
                    onComposerDrop={m.handleComposerDrop}
                    onComposerPaste={m.handleComposerPaste}
                    /* Edit mode: cheap flags only — the edited text lives inside the composer. */
                    editing={Boolean(m.editingMessage)}
                    editingKey={m.editingMessage?.id || ""}
                    editingScope={m.editingMessage?.kind || ""}
                    editingHasAttachments={Boolean(m.editingMessage?.hasAttachments)}
                    onCancelEdit={m.handleCancelEdit}
                    onTypingActivity={m.isInternalScope ? m.notifyComposerTyping : undefined}
                    mentionCandidates={m.isInternalScope ? mentionCandidates : undefined}
                    /* Message templates: variables are resolved for the thread's patient,
                       and {{whatsappNumberLabel}} names the number we're replying from. */
                    templatePatientUserId={m.selectedThreadForHeader?.patientUserId || ''}
                    templateWhatsappNumberLabel={
                      m.mergedReplySource?.label || m.selectedThreadForHeader?.whatsappNumberLabel || ''
                    }
                    {...(import.meta.env.DEV
                      ? {
                          devSendAsPatient: m.devSendAsPatient,
                          onDevSendAsPatientChange: m.setDevSendAsPatient,
                          devPatientImpersonationUserId: m.devPatientImpersonationUserId,
                          devPatientImpersonationLabel: m.devPatientImpersonationLabel,
                        }
                      : {})}
                  />
                  )}
                </div>
                </ResizablePanel>
                {/* Hidden below md, like the panel itself used to be — otherwise the empty
                    ResizablePanel would keep reserving width on small screens. */}
                <ResizableHandle withHandle className="hidden md:flex" />
                <ResizablePanel
                  panelRef={rightPanelRef}
                  collapsible
                  collapsedSize={`${RIGHT_PANEL_COLLAPSED_WIDTH_PX}px`}
                  defaultSize={initialRightPanelSize}
                  minSize="240px"
                  maxSize="600px"
                  onResize={handleRightPanelResize}
                  className="hidden min-w-0 md:block"
                >
                {tasksPanelShowing && headerIsTriageScope ? (
                  <ThreadTasksPanel
                    thread={m.selectedThreadForHeader}
                    listClockMs={m.listClockMs}
                    tasks={m.threadTasks}
                    tasksLoading={m.threadTasksLoading}
                    onClose={() => m.setRightPanelOpen(false)}
                    onShowParticipants={() => m.setRightPanelMode("participants")}
                    onThreadFlagChange={m.handleThreadFlagCheckedChange}
                    onClearAiHandling={m.handleAiHandlingClear}
                    // DISABLED (for now): manual status override + snooze — the panel's
                    // Status/Snooze sections are hidden.
                    // onStatusChange={m.handleStatusChange}
                    // DISABLED (for now): thread claim/release — assignment lives on tasks only.
                    // onClaimThread={m.handleClaimThread}
                    // onReleaseThread={m.handleReleaseThread}
                    // onSnoozeThread={m.handleSnoozeThread}
                    onTaskCreate={m.handleTaskCreate}
                    onTaskToggle={m.handleTaskToggle}
                    onTaskClaim={m.handleTaskClaim}
                    onTaskAssign={m.handleTaskAssign}
                    onTaskDelete={m.handleTaskDelete}
                    assignableUsers={m.threadMembersGrouped?.team || []}
                  />
                ) : (
                  <ThreadParticipantsPanel
                    isInternalScope={m.isScopedList}
                    rightPanelOpen={m.rightPanelOpen}
                    onRightPanelOpenChange={(open) => {
                      m.setRightPanelMode("participants");
                      m.setRightPanelOpen(open);
                    }}
                    showTasksTab={headerIsTriageScope}
                    onShowTasks={() => m.setRightPanelMode("tasks")}
                    tasksBadge={headerOpenTaskCount}
                    threadMembersNotice={m.threadMembersNotice}
                    threadMembersError={m.threadMembersError}
                    membersPanelLoading={m.membersPanelLoading}
                    showEmptyMembersPanel={m.showEmptyMembersPanel}
                    memberListSource={m.memberListSource}
                    threadMembersGrouped={m.threadMembersGrouped}
                    historyDerivedMemberRows={m.historyDerivedMemberRows}
                    waGroupInfo={m.activeThreadWaGroupInfoForDisplay}
                    onMemberUserClick={m.handleMemberUserClick}
                    collapsedSections={m.participantsCollapsedSections}
                    onToggleSection={m.toggleParticipantsSection}
                    presenceOnlineIds={m.isInternalScope ? m.onlineUserIds : null}
                    presenceAvailability={m.isInternalScope ? m.availabilityByUserId : null}
                    onLeaveGroup={
                      m.isInternalScope && m.headerIsInternalGroup
                        ? () => {
                            const threadId = String(m.selectedThreadForHeader?.threadId || "").trim();
                            if (!threadId) return;
                            const confirmed = window.confirm(
                              "Leave this group? You'll stop receiving its messages.",
                            );
                            if (!confirmed) return;
                            void m.handleLeaveGroup(threadId);
                          }
                        : undefined
                    }
                  />
                )}
                </ResizablePanel>
              </ResizablePanelGroup>
            )}
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      {canUseAiRewrite ? (
      <DraftReplyAiDialog
        open={m.draftReplyAiModalOpen}
        onOpenChange={m.handleDraftReplyAiModalOpenChange}
        sourceText={m.draftReplyAiModalSource}
        onSourceChange={m.setDraftReplyAiModalSource}
        outputText={m.draftReplyAiModalOutput}
        onOutputChange={m.setDraftReplyAiModalOutput}
        generating={m.draftReplyAiGenerating}
        onGenerate={m.generateDraftReplyAi}
        onApply={m.applyDraftReplyAiModal}
        personas={m.draftReplyAiPersonas}
        switchDefs={m.draftReplyAiSwitchDefs}
        persona={m.draftReplyAiPersona}
        onPersonaChange={m.setDraftReplyAiPersona}
        options={m.draftReplyAiOptions}
        onOptionChange={m.toggleDraftReplyAiOption}
      />
      ) : null}
      <FlowSessionResetDialog
        open={m.flowSessionResetDialogOpen}
        onOpenChange={m.handleFlowSessionResetDialogOpenChange}
        onConfirmYes={m.confirmFlowSessionReset}
      />
      <DeleteThreadDialog
        open={Boolean(m.deleteThreadTarget)}
        onOpenChange={m.handleDeleteThreadDialogOpenChange}
        onConfirmYes={m.confirmDeleteThread}
        threadTitle={m.deleteThreadTarget?.title}
        inProgress={m.deleteThreadInProgress}
      />
      <RevokeMessageDialog
        open={Boolean(revokeTarget)}
        onOpenChange={(open) => {
          if (!open) setRevokeTarget(null);
        }}
        onConfirmYes={handleConfirmDeleteForEveryone}
        preview={getMessageText(revokeTarget)}
        variant={m.activeThreadIsWhatsApp ? "whatsapp" : "internal"}
      />
      <ThreadMemberDetailsDialog
        open={m.threadMemberDetailsOpen}
        onOpenChange={m.onThreadMemberDetailsOpenChange}
        user={m.threadMemberDetailsUser}
        avatarUrl={m.threadMemberDetailsAvatarUrl}
        onRefreshPhoneDuplicates={
          m.threadMemberDetailsAllowPhoneDuplicates ? m.handleRefreshUserPhoneDuplicates : undefined
        }
        refreshingPhoneDuplicates={m.refreshingPhoneDuplicates}
        onSaveCorporateInsurance={m.handleSaveCorporateInsurance}
        savingCorporateInsurance={m.savingCorporateInsurance}
      />
      {headerPatientUserId ? (
        <PatientEditorDialog
          open={patientEditorOpen}
          onOpenChange={setPatientEditorOpen}
          userId={headerPatientUserId}
          onSaved={() => {
            // Refresh the member snapshots so the header tags + participants panel reflect the edit.
            if (headerThreadId) void m.loadAdminThreadMembers(headerThreadId);
          }}
        />
      ) : null}
      {headerThreadId ? (
        <SwitchPatientDialog
          open={switchPatientOpen}
          onOpenChange={setSwitchPatientOpen}
          threadId={headerThreadId}
          currentPatientName={m.conversationHeaderTitle}
          onSwitched={() => {
            if (headerThreadId) void m.loadAdminThreadMembers(headerThreadId);
            void m.loadThreads();
          }}
        />
      ) : null}
      <AmbiguousMigrationDialog
        open={m.ambiguousMigrationModal.open}
        onOpenChange={(next) => {
          if (!next) m.closeAmbiguousMigrationModal();
        }}
        modal={m.ambiguousMigrationModal}
        setModal={m.setAmbiguousMigrationModal}
        onConfirmChoice={m.handleConfirmAmbiguousMigrationChoice}
        onConfirmPatientCorrect={m.handleConfirmPatientCorrect}
      />
      <MergeResultDialog
        open={m.mergeResultModal.open}
        onOpenChange={(next) => {
          if (!next) m.closeMergeResultModal();
        }}
        success={m.mergeResultModal.success}
        title={m.mergeResultModal.title}
        description={m.mergeResultModal.description}
        sourceName={m.mergeResultModal.sourceName}
        targetName={m.mergeResultModal.targetName}
      />
      <Dialog open={headerPhotoModalOpen} onOpenChange={setHeaderPhotoModalOpen}>
        <DialogContent className="w-[92vw] max-w-3xl border-none bg-transparent p-0 shadow-none">
          {headerPhotoUrl ? (
            <img
              src={headerPhotoUrl}
              alt={`${m.conversationHeaderTitle} profile`}
              className="mx-auto max-h-[80vh] w-auto max-w-full rounded-xl object-contain"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function getThreadId(thread) {
  return String(thread?.threadId || thread?.id || "");
}

const EMPTY_SECTIONS = new Set();

function SectionHeader({
  sectionKey,
  collapsed,
  onToggle,
  className,
  children,
  rightSlot,
}) {
  const interactive = typeof onToggle === "function" && Boolean(sectionKey);
  return (
    <button
      type="button"
      onClick={interactive ? () => onToggle(sectionKey) : undefined}
      aria-expanded={!collapsed}
      disabled={!interactive}
      className={cn(
        "flex w-full items-center justify-between gap-2 px-3 pb-1.5 pt-2 text-left text-[10px] font-bold uppercase tracking-wider transition",
        interactive ? "cursor-pointer hover:brightness-95 dark:hover:brightness-110" : "cursor-default",
        className,
      )}
    >
      <span className="flex min-w-0 items-center gap-1.5">
        {interactive ? (
          collapsed ? (
            <ChevronRight className="h-3 w-3 shrink-0 opacity-70" strokeWidth={2.5} />
          ) : (
            <ChevronDown className="h-3 w-3 shrink-0 opacity-70" strokeWidth={2.5} />
          )
        ) : null}
        {children}
      </span>
      {rightSlot}
    </button>
  );
}

const SegmentedThreadList = memo(function SegmentedThreadList({
  threads,
  lens = "all",
  serverWaiting = null,
  serverPatientReply = null,
  serverBotAck = null,
  listClockMs,
  botAckThresholdMinutes,
  botAckMaxHours,
  patientReplyThresholdMinutes,
  patientReplyMaxHours,
  attentionTruncated = false,
  threadMemberIdsByThreadId,
  selectedThreadId,
  localUnreadByThreadId,
  onSelectThread,
  onThreadFlagChange,
  groups = [],
  threadListView = "standard",
  onThreadGroupChange,
  // DISABLED (for now): thread claim — assignment lives on tasks only.
  // onClaimThread,
  onPatientTagToggle,
  onStatusChange,
  onDeleteThread,
  // Internal Chats row actions (undefined in Messages scope).
  onMarkThreadRead,
  onMarkThreadUnread,
  onToggleMute,
  onLeaveThread,
  onDeleteChat,
  mutedThreadIds = null,
  collapsedSections = EMPTY_SECTIONS,
  onToggleSection,
  onlineUserIds = null,
  availabilityByUserId = null,
  typingThreadIds = null,
  isInternalScope = false,
  showWhatsappNumber = false,
  scrollElementRef = null,
}) {
  // Resolved once per list render (not once per row) and handed to every row via rowProps.
  const currentUserId = String(getAuthUser()?.id || "").trim();
  const rowProps = {
    listClockMs,
    lens,
    selectedThreadId,
    localUnreadByThreadId,
    currentUserId,
    onSelectThread,
    onThreadFlagChange,
    groups,
    onThreadGroupChange,
    // onClaimThread,
    onPatientTagToggle,
    onStatusChange,
    onDeleteThread,
    onMarkThreadRead,
    onMarkThreadUnread,
    onToggleMute,
    onLeaveThread,
    onDeleteChat,
    mutedThreadIds,
    onlineUserIds,
    availabilityByUserId,
    typingThreadIds,
    isInternalScope,
    showWhatsappNumber,
  };

  // Flatten the active lens/view into ONE ordered list of items (section/day headers + rows) so the
  // whole list can be virtualized — only the visible window mounts instead of all N rows (~26k DOM
  // nodes at 859 threads). `sticky: true` marks the chronological day separators, the only headers
  // that pin to the top while scrolling (needs/groups section headers scroll normally, as before).
  const items = useMemo(() => {
    const out = [];
    let rowIndex = 0;
    const pushRow = (thread, extra) => {
      out.push({
        type: "row",
        key: `row:${getThreadId(thread)}`,
        thread,
        extra,
        rowIndex: rowIndex++,
      });
    };

    // Chronological lenses fold the server-fed attention rows INTO the flat list (deduped by id,
    // newest first) so a flagged thread outside the loaded page/window never vanishes when leaving
    // the "Needs you" lens.
    const buildUnionRows = () => {
      const ids = new Set(threads.map((t) => getThreadId(t)));
      const union = [...threads];
      for (const source of [serverWaiting, serverPatientReply, serverBotAck]) {
        if (!Array.isArray(source)) continue;
        for (const thread of source) {
          const tid = getThreadId(thread);
          if (!tid || ids.has(tid)) continue;
          ids.add(tid);
          union.push(thread);
        }
      }
      union.sort(
        (a, b) => new Date(b?.lastMessageAt || 0).getTime() - new Date(a?.lastMessageAt || 0).getTime(),
      );
      return union;
    };

    // ---- "Needs you" lens: the triage piles, and only them. -------------------------------
    // With server lists (normal scope, no search) the attention sections come from the overview
    // endpoint and are COMPLETE — they include qualifying threads far outside the loaded window.
    // The predicates still run against the ticking clock so threads age in/out between refreshes.
    if (lens === "needs") {
      const waiting = [];
      const patientReply = [];
      const botAck = [];
      const waitingSource = Array.isArray(serverWaiting) ? serverWaiting : threads;
      const patientReplySource = Array.isArray(serverPatientReply) ? serverPatientReply : threads;
      const botAckSource = Array.isArray(serverBotAck) ? serverBotAck : threads;
      const sectionIds = new Set();
      for (const thread of waitingSource) {
        const tid = getThreadId(thread);
        const membership = threadMemberIdsByThreadId[tid];
        const min = getWhatsAppInboxWaitingMinutes(thread, membership ?? null, listClockMs);
        if (min != null && !sectionIds.has(tid)) {
          sectionIds.add(tid);
          waiting.push({ thread, min });
        }
      }
      // Patient replies before bot replies: a patient nobody answered is the most urgent.
      for (const thread of patientReplySource) {
        const tid = getThreadId(thread);
        if (sectionIds.has(tid)) continue; // waiting wins on overlap
        const min = getPatientReplyWaitingMinutes(
          thread,
          patientReplyThresholdMinutes,
          patientReplyMaxHours,
          listClockMs,
        );
        if (min != null) {
          sectionIds.add(tid);
          patientReply.push({ thread, min });
        }
      }
      for (const thread of botAckSource) {
        const tid = getThreadId(thread);
        if (sectionIds.has(tid)) continue; // waiting / patient reply win on overlap
        const min = getBotAckWaitingMinutes(thread, botAckThresholdMinutes, botAckMaxHours, listClockMs);
        if (min != null) {
          sectionIds.add(tid);
          botAck.push({ thread, min });
        }
      }
      waiting.sort((a, b) => b.min - a.min);
      patientReply.sort((a, b) => b.min - a.min);
      botAck.sort((a, b) => b.min - a.min);
      const worstMin = waiting[0]?.min ?? 0;
      const worstTier = waitTier(worstMin);
      const worstTierStyles = TIER_STYLES[worstTier] ?? TIER_STYLES[0];
      const worstPatientReplyMin = patientReply[0]?.min ?? 0;
      const worstBotAckMin = botAck[0]?.min ?? 0;
      const waitingCollapsed = collapsedSections.has("waiting");
      const patientReplyCollapsed = collapsedSections.has("patientReply");
      const botAckCollapsed = collapsedSections.has("botAck");

      if (waiting.length > 0) {
        out.push({
          type: "header",
          key: "sec:waiting",
          node: (
            <SectionHeader
              sectionKey="waiting"
              collapsed={waitingCollapsed}
              onToggle={onToggleSection}
              className={cn(
                worstTierStyles.sectionBg,
                worstTier >= 2 ? worstTierStyles.sectionText : "text-muted-foreground",
              )}
              rightSlot={
                worstMin > 0 ? (
                  <span className={cn("font-mono text-[11px] normal-case", worstTierStyles.sectionText)}>
                    oldest {formatWaitShort(worstMin)}
                  </span>
                ) : null
              }
            >
              <span>Waiting for you · {waiting.length}</span>
            </SectionHeader>
          ),
        });
        if (!waitingCollapsed) waiting.forEach(({ thread }) => pushRow(thread, undefined));
      }
      if (patientReply.length > 0) {
        out.push({
          type: "header",
          key: "sec:patientReply",
          node: (
            <SectionHeader
              sectionKey="patientReply"
              collapsed={patientReplyCollapsed}
              onToggle={onToggleSection}
              className="bg-rose-100 text-rose-900 dark:bg-rose-950/50 dark:text-rose-100"
              rightSlot={
                <span className="font-mono text-[11px] normal-case text-rose-900/80 dark:text-rose-100/80">
                  oldest {formatWaitShort(worstPatientReplyMin)}
                </span>
              }
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" />
              <span>Unanswered patient reply · {patientReply.length}</span>
            </SectionHeader>
          ),
        });
        if (!patientReplyCollapsed)
          patientReply.forEach(({ thread, min }) => pushRow(thread, { patientReplyMinutes: min }));
      }
      if (botAck.length > 0) {
        out.push({
          type: "header",
          key: "sec:botAck",
          node: (
            <SectionHeader
              sectionKey="botAck"
              collapsed={botAckCollapsed}
              onToggle={onToggleSection}
              className="bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-100"
              rightSlot={
                <span className="font-mono text-[11px] normal-case text-amber-900/80 dark:text-amber-100/80">
                  oldest {formatWaitShort(worstBotAckMin)}
                </span>
              }
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
              <span>Unanswered bot replies · {botAck.length}</span>
            </SectionHeader>
          ),
        });
        if (!botAckCollapsed)
          botAck.forEach(({ thread, min }) => pushRow(thread, { botAckMinutes: min }));
      }
      // The server capped its candidate scan, so the sections above may be missing their oldest
      // rows. Say so — an unexplained short triage list reads as "nothing left to do".
      if (attentionTruncated) {
        out.push({
          type: "header",
          key: "hdr:attention-truncated",
          node: (
            <div className="px-3 py-1.5 text-[10px] text-muted-foreground">
              Some older threads may be missing — this inbox exceeded the server's attention limit.
            </div>
          ),
        });
      }
      return out;
    }

    // ---- "All" lens: everything, newest first — the calm default. -------------------------
    const unionRows = buildUnionRows();

    if (threadListView !== "groups") {
      for (const dayGroup of groupThreadsByDay(unionRows, listClockMs)) {
        out.push({
          type: "header",
          sticky: true,
          key: `day:${dayGroup.key}`,
          node: (
            <div className="flex items-center gap-2 border-b border-border/40 bg-background/95 px-3 py-1.5 backdrop-blur dark:bg-background/90">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {dayGroup.label}
              </span>
              <span className="h-px min-w-4 flex-1 bg-border/70" />
              {dayGroup.dateLabel ? (
                <span className="font-mono text-[10px] text-muted-foreground/80">{dayGroup.dateLabel}</span>
              ) : null}
            </div>
          ),
        });
        dayGroup.threads.forEach((thread) => pushRow(thread, undefined));
      }
      return out;
    }

    // Groups view: per-group blocks (configured order), then "All threads" (ungrouped only). Each
    // thread appears in exactly one block; unknown group ids fall through to "All threads".
    const allCollapsed = collapsedSections.has("all");
    const validGroupIds = new Set(groups.map((g) => g.id));
    const byGroupId = new Map();
    for (const g of groups) byGroupId.set(g.id, []);
    const ungrouped = [];
    for (const thread of unionRows) {
      const gid = getThreadGroupId(thread);
      if (gid && validGroupIds.has(gid)) byGroupId.get(gid).push(thread);
      else ungrouped.push(thread);
    }
    for (const group of groups) {
      const groupThreads = byGroupId.get(group.id) || [];
      if (groupThreads.length === 0) continue;
      const color = getGroupColor(group.colorId);
      const sectionKey = `group:${group.id}`;
      const groupCollapsed = collapsedSections.has(sectionKey);
      out.push({
        type: "header",
        key: `hdr:${sectionKey}`,
        node: (
          <div className="border-y border-border/40">
            <SectionHeader
              sectionKey={sectionKey}
              collapsed={groupCollapsed}
              onToggle={onToggleSection}
              className={cn("py-1.5", color.sectionClassName)}
              rightSlot={
                <span className="font-mono text-[11px] normal-case opacity-80">{groupThreads.length}</span>
              }
            >
              <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", color.dotClassName)} />
              <span className="truncate">{group.name}</span>
              {groupHasFlag(group, THREAD_GROUP_FLAG_IGNORE) ? (
                <BotOff className="h-3 w-3 shrink-0 opacity-70" title="Bot ignores this group" aria-hidden />
              ) : null}
            </SectionHeader>
          </div>
        ),
      });
      // The section header already names the group — per-row group chips would repeat it.
      if (!groupCollapsed) groupThreads.forEach((thread) => pushRow(thread, { hideGroupChip: true }));
    }
    if (ungrouped.length > 0) {
      out.push({
        type: "header",
        key: "hdr:all",
        node: (
          <SectionHeader
            sectionKey="all"
            collapsed={allCollapsed}
            onToggle={onToggleSection}
            className="text-muted-foreground"
          >
            <span>All threads · {ungrouped.length}</span>
          </SectionHeader>
        ),
      });
      if (!allCollapsed) ungrouped.forEach((thread) => pushRow(thread, undefined));
    }
    return out;
  }, [
    threads,
    serverWaiting,
    serverPatientReply,
    serverBotAck,
    listClockMs,
    lens,
    threadListView,
    groups,
    collapsedSections,
    threadMemberIdsByThreadId,
    botAckThresholdMinutes,
    botAckMaxHours,
    patientReplyThresholdMinutes,
    patientReplyMaxHours,
    attentionTruncated,
    onToggleSection,
  ]);

  // Day separators pin to the top while their group scrolls: rangeExtractor always keeps the active
  // one in the rendered window and it renders position:sticky (instead of translated).
  const stickyIndexes = useMemo(() => {
    const acc = [];
    items.forEach((it, i) => {
      if (it.sticky) acc.push(i);
    });
    return acc;
  }, [items]);
  const activeStickyIndexRef = useRef(-1);
  const rangeExtractor = useCallback(
    (range) => {
      let active = -1;
      for (const i of stickyIndexes) {
        if (i <= range.startIndex) active = i;
        else break;
      }
      activeStickyIndexRef.current = active;
      const base = defaultRangeExtractor(range);
      if (active < 0) return base;
      return Array.from(new Set([active, ...base])).sort((a, b) => a - b);
    },
    [stickyIndexes],
  );

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => (scrollElementRef ? scrollElementRef.current : null),
    estimateSize: (i) => (items[i]?.type === "header" ? 34 : 72),
    overscan: 12,
    getItemKey: (i) => items[i]?.key ?? i,
    rangeExtractor,
  });

  if (items.length === 0) {
    // Reachable only for the "Needs you" lens (other lenses render only when data exists).
    if (lens === "needs") {
      return (
        <div className="flex flex-col items-center gap-1 px-4 py-12 text-center">
          <p className="text-sm font-medium text-foreground">Nothing needs you right now</p>
          <p className="text-xs text-muted-foreground">
            Locked and staff-handled conversations land here, along with unanswered patient and bot
            replies.
          </p>
        </div>
      );
    }
    return null;
  }

  return (
    <div style={{ position: "relative", width: "100%", height: `${virtualizer.getTotalSize()}px` }}>
      {virtualizer.getVirtualItems().map((virtualRow) => {
        const item = items[virtualRow.index];
        if (!item) return null;
        const isHeader = item.type === "header";
        const activeSticky = Boolean(item.sticky) && activeStickyIndexRef.current === virtualRow.index;
        return (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            {...(item.type === "row" ? { "data-thread-item": "" } : {})}
            ref={virtualizer.measureElement}
            style={{
              position: activeSticky ? "sticky" : "absolute",
              top: 0,
              left: 0,
              width: "100%",
              zIndex: isHeader ? (activeSticky ? 12 : 10) : undefined,
              transform: activeSticky ? undefined : `translateY(${virtualRow.start}px)`,
            }}
          >
            {isHeader ? (
              item.node
            ) : (
              <ThreadListItem
                thread={item.thread}
                index={item.rowIndex}
                threadMembership={threadMemberIdsByThreadId[getThreadId(item.thread)]}
                {...rowProps}
                {...item.extra}
              />
            )}
          </div>
        );
      })}
    </div>
  );
});
